import React, { useState, useEffect, useCallback, useRef } from 'react';
import { View, Text, StyleSheet, Pressable, TextInput, ScrollView, Icon3 } from '../lib/react-native-compat';
import { useAtom } from 'jotai';
import {
    selectedJobAtom,
    selectedVersionAtom,
    selectedVersionLockedAtom,
    jobUserDataAtom,
    jobPhaseCodesAtom,
    userNameAtom,
    userIdAtom,
    ohPercentAtom,
    overheadDataAtom,
    selectedTeamAtom,
    sideMenuBudgetsAtom,
} from '../store/company';
import { updateUserDataArray, flushWriteQueue, pNum, fNum, deleteCO, deleteCOByRowLabel, deleteUserData, loadGLAccount } from '../db/company';
import { loadResourceCodeTables } from '../helpers/resourceCodeDb';
import { getCoDocs, verifyCoSignature } from './cologDocsApi';
import moment from 'moment';
import XLSX from 'xlsx';

/**
 * COLogEntry - Manual UI entry for Change Orders
 * 
 * One UI row = one Change Order, but saving produces multiple DB rows:
 *   - 1 "Change Order Header" row (metadata: description, status, type, dates, etc.)
 *   - N "Change Order" rows (one per cost type with dollars/hours/qty)
 * 
 * Metadata (status, description, type, dates, notes) is preserved across all rows.
 */

// Normalize a CO label or SharePoint folder/file name to a comparable key so a
// CO row can be matched to its document folder:
//   "CO-02 - VRU Transfer" → "C2", "02" → "C2", "I-01" → "I1"
const coDocKey = (s) => {
    const str = String(s || '').trim().toUpperCase();
    let m = str.match(/^I[-\s]*0*(\d+)/);          // internal change: "I-01"
    if (m) return 'I' + parseInt(m[1], 10);
    m = str.match(/^(?:CO)?[-\s]*0*(\d+)/);         // regular CO, optional "CO" prefix
    if (m) return 'C' + parseInt(m[1], 10);
    return null;
};

// Same idea, but for a SharePoint document/folder NAME rather than a CO row
// label. Filed docs embed the CO token mid-name — e.g.
//   "LARO-WHCA Project- CO-001 - signed.pdf"
// so we scan for a CO-/I- token anywhere in the string instead of anchoring at
// the start (which only fit bare row labels like "02" / "CO-02 - ...").
const coDocKeyFromName = (s) => {
    const str = String(s || '').toUpperCase();
    let m = str.match(/\bI[-\s]*0*(\d+)/);          // internal change: "... I-01 ..."
    if (m) return 'I' + parseInt(m[1], 10);
    m = str.match(/\bCO[-\s]*0*(\d+)/);             // regular CO: "... CO-001 ..."
    if (m) return 'C' + parseInt(m[1], 10);
    return null;
};

// Cap CO descriptions so they fit on one line in the collapsed row and PSR views
const MAX_DESCRIPTION_LENGTH = 120;

// Searchable combobox for Resource Code. Wraps a plain <input> plus an
// absolute-positioned dropdown so the user can type to filter a long list.
const ResourceCodeSelect = ({ value, options, onChange, width = 110, placeholder = '-- Resource --' }) => {
    const [typing, setTyping] = useState(false);
    const [search, setSearch] = useState('');
    const selected = options.find(o => o.value === value);
    const display = typing ? search : (selected ? selected.label : (value || ''));
    const searchLower = search.toLowerCase();
    const filtered = typing && search
        ? options.filter(o => o.label.toLowerCase().includes(searchLower))
        : options;

    return (
        <View style={{ position: 'relative', width }}>
            <input
                type="text"
                value={display}
                placeholder={placeholder}
                onFocus={() => { setSearch(''); setTyping(true); }}
                onBlur={() => setTimeout(() => setTyping(false), 150)}
                onChange={(e) => { setSearch(e.target.value); setTyping(true); }}
                style={{
                    width: '100%', height: 28, fontSize: 12,
                    borderRadius: 4, border: '1px solid #cbd5e1',
                    paddingLeft: 6, paddingRight: 4, boxSizing: 'border-box',
                }}
            />
            {typing && (
                <div style={{
                    position: 'absolute', top: 28, left: 0, width: 280,
                    maxHeight: 240, overflowY: 'auto',
                    background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100,
                }}>
                    {value ? (
                        <div
                            onMouseDown={(e) => { e.preventDefault(); onChange(''); setSearch(''); setTyping(false); }}
                            style={{ padding: '4px 8px', fontSize: 11, cursor: 'pointer', borderBottom: '1px solid #f1f5f9', color: '#64748b', fontStyle: 'italic' }}
                        >
                            -- Clear --
                        </div>
                    ) : null}
                    {filtered.length === 0 ? (
                        <div style={{ padding: '6px 8px', fontSize: 11, color: '#94a3b8' }}>No matches</div>
                    ) : filtered.map(o => (
                        <div
                            key={o.value}
                            onMouseDown={(e) => { e.preventDefault(); onChange(o.value); setSearch(''); setTyping(false); }}
                            style={{
                                padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                backgroundColor: o.value === value ? '#e0f2fe' : '#fff',
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = o.value === value ? '#e0f2fe' : '#fff'}
                        >
                            {o.label}
                        </div>
                    ))}
                </div>
            )}
        </View>
    );
};

const COLogEntry = () => {
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [lockedVersion] = useAtom(selectedVersionLockedAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [jobPhaseCodes] = useAtom(jobPhaseCodesAtom);
    const [userName] = useAtom(userNameAtom);
    const [userId] = useAtom(userIdAtom);
    const [ohPercent] = useAtom(ohPercentAtom);
    const [overheadData] = useAtom(overheadDataAtom);
    const [selectedTeam] = useAtom(selectedTeamAtom);
    const [smBudgets] = useAtom(sideMenuBudgetsAtom);
    const [glValues, setGlValues] = useState([]);
    const [resourceCodeOptions, setResourceCodeOptions] = useState([]);

    const [coEntries, setCoEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null); // localId of entry to confirm delete
    const [expandedCards, setExpandedCards] = useState({}); // { localId: true/false } - collapsed by default
    const [coDocsInfo, setCoDocsInfo] = useState(null); // SharePoint change-order docs for this job
    const [sigResults, setSigResults] = useState({});   // { [coNo]: verification result } — persisted in Forecast_User_Data
    const [sigRunning, setSigRunning] = useState({});   // { [coNo]: true } while a verify call is in flight

    // Pull the job's filed change-order documents so we can flag COs with none.
    useEffect(() => {
        let cancelled = false;
        if (!selectedJob) { setCoDocsInfo(null); return; }
        getCoDocs(selectedJob)
            .then((info) => { if (!cancelled) setCoDocsInfo(info); })
            .catch(() => { if (!cancelled) setCoDocsInfo(null); });
        return () => { cancelled = true; };
    }, [selectedJob]);

    // A CO is "documented" when the SharePoint Change Orders folder has a
    // subfolder (or file) whose CO number matches — e.g. row 02 ↔ "CO-02 - ...".
    const documentedCoKeys = React.useMemo(() => {
        const set = new Set();
        if (coDocsInfo) {
            [...(coDocsInfo.subfolders || []), ...(coDocsInfo.files || [])].forEach((it) => {
                const k = coDocKeyFromName(it.name);
                if (k) set.add(k);
            });
        }
        return set;
    }, [coDocsInfo]);

    // Warn only for customer-facing COs with no matching document. Internal
    // Change rows (I-xx) never require a SharePoint document, so they don't warn.
    const isCoMissingDoc = (entry) => {
        if (!coDocsInfo) return false;                  // not loaded — don't false-alarm
        const key = coDocKey(entry.coNo);
        if (!key) return false;                         // unsaved/blank CO number
        if (key[0] === 'I') return false;               // internal change — exempt
        if (/internal/i.test(entry.type || '')) return false;
        return !documentedCoKeys.has(key);
    };
    const coDocsTooltip = 'No change-order document filed in SharePoint (03.09 - Change Orders)';

    // Contract-value document status (03.01 - Contract). Only meaningful once
    // the SharePoint lookup has come back and the job subsite exists.
    const contractStatus = React.useMemo(() => {
        if (!coDocsInfo || !coDocsInfo.siteFound) return null;
        if ((coDocsInfo.contractFileCount || 0) > 0) {
            return {
                ok: true,
                label: 'Contract filed in SharePoint',
                tooltip: (coDocsInfo.contractFiles || []).map((f) => f.name).join('\n'),
                url: coDocsInfo.contractFolderUrl,
            };
        }
        return {
            ok: false,
            label: coDocsInfo.contractFolderFound
                ? 'No contract doc in SharePoint'
                : 'No 03.01 - Contract folder in SharePoint',
            tooltip: 'The executed contract (contract value) has not been filed under 03 - Project Management / 03.01 - Contract',
            url: coDocsInfo.contractFolderUrl,
        };
    }, [coDocsInfo]);

    // ── Signature verification (persisted in Forecast_User_Data) ──────────────
    // The result is stored as its own field-row under a distinct tab so it never
    // clobbers the Change Order Header row on load (header grouping keys on
    // Row_Label, so a same-label row under "Change Order Header" would overwrite
    // the real header). Read back in loadEntries → sigResults, keyed by CO number.
    const SIG_TAB = 'Change Order Signature';
    const SIG_FIELD = 'signatureVerification';

    // Compact result kept in the Notes column (<= 1024 chars).
    const compactSig = (r, signedDate) => ({
        status: r.status,
        source: r.source || null,          // 'form' | 'email' | null
        clientSigned: r.clientSigned,
        docDate: r.docDate,
        dateSource: r.dateSource,
        dateMatches: r.dateMatches,
        fileName: r.fileName,
        emailApprover: r.emailApprover || null,
        confidence: r.confidence,
        signedDate: signedDate || r.signedDate || null,
        verifiedAt: new Date().toISOString(),
    });

    // Run the vision verifier for one CO and persist the result via
    // updateUserDataArray. Internal-change COs are exempt (never require a signed
    // document), so they're skipped — matching the missing-doc warning logic.
    const runAndPersistVerification = async (coNo, signedDate, type, description) => {
        const key = coDocKey(coNo);
        if (!coNo || !key) return;
        if (key[0] === 'I' || /internal/i.test(type || '')) return; // internal — exempt
        setSigRunning(prev => ({ ...prev, [coNo]: true }));
        try {
            const result = await verifyCoSignature({ job: selectedJob, co: coNo, signedDate, description });
            if (!result) return;
            const compact = compactSig(result, signedDate);
            setSigResults(prev => ({ ...prev, [coNo]: compact }));
            await updateUserDataArray([{
                userId: userId || '', userName: userName || '',
                tab: SIG_TAB,
                rowId: `${coNo}_signature`,
                version: selectedVersion + '',
                id: selectedJob,
                lockedVersion,
                fieldName: SIG_FIELD,
                value: result.status,
                status: result.status,
                rowLabel: coNo,
                notes: JSON.stringify(compact),
                forecastDate: compact.verifiedAt,
                // These five placeholders are substituted unconditionally by the
                // Azure Function, so omitting them writes the literal 'undefined'.
                costType: '', costCode: '', vendor: '', po: '', equipment: '',
            }]);
        } catch (err) {
            console.error('CO signature verification failed:', err);
            setSigResults(prev => ({ ...prev, [coNo]: { status: 'error', error: err.message } }));
        } finally {
            setSigRunning(prev => ({ ...prev, [coNo]: false }));
        }
    };

    // Compact status badge + manual re-run control shown under the Signed Date
    // input. Internal-change COs never require a signed document, so they show
    // nothing. Full detail (doc date, source, file) lives in the hover title.
    const renderSigBadge = (entry) => {
        const coNo = entry.coNo ? entry.coNo.toString() : '';
        if (!coNo) return null;
        const key = coDocKey(coNo);
        if (key && key[0] === 'I') return null;
        if (/internal/i.test(entry.type || '')) return null;

        const running = !!sigRunning[coNo];
        const res = sigResults[coNo];

        let color = '#6b7280', icon = 'MdHelpOutline', label = 'Verify', title = 'Run signature verification';
        if (running) {
            color = '#2563eb'; icon = 'MdRefresh'; label = 'Verifying…'; title = 'Verifying signature…';
        } else if (res) {
            title = `${res.status}` +
                (res.source ? ` · via ${res.source}` : '') +
                (res.docDate ? ` · doc date ${res.docDate} (${res.dateSource || '?'})` : '') +
                (res.emailApprover ? ` · ${res.emailApprover}` : '') +
                (res.fileName ? ` · ${res.fileName}` : '');
            if (res.status === 'signed') {
                color = '#16a34a'; icon = 'MdCheckCircle';
                label = res.source === 'email'
                    ? 'Approved · email'
                    : (res.dateMatches === true ? 'Signed · date OK' : 'Signed');
            } else if (res.status === 'date_mismatch') {
                color = '#f59e0b'; icon = 'MdWarning'; label = 'Date differs';
            } else if (res.status === 'unsigned') {
                color = '#dc2626'; icon = 'MdError'; label = 'Not signed';
            } else if (res.status === 'no_doc') {
                color = '#f59e0b'; icon = 'MdWarning'; label = 'No signed doc';
            } else if (res.status === 'error') {
                color = '#dc2626'; icon = 'MdError'; label = 'Verify failed';
            }
        }

        return (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }} title={title}>
                <Icon3 name={icon} size={13} color={color} />
                <Text style={{ marginLeft: 3, fontSize: 10, color }}>{label}</Text>
                {!running && (
                    <Pressable
                        onPress={() => runAndPersistVerification(coNo, entry.signed, entry.type, entry.description)}
                        style={{ marginLeft: 6 }}
                        title="Re-run signature verification"
                    >
                        <Icon3 name="MdRefresh" size={13} color="#6b7280" />
                    </Pressable>
                )}
            </View>
        );
    };

    const fileInputRef = useRef();
    const fileInputV2Ref = useRef();
    const autoSaveTimerRef = useRef(null);
    const saveInProgressRef = useRef(false);   // prevents concurrent saveEntry calls
    const pendingSaveEntryRef = useRef(null);   // queued entry to save after current finishes
    const coEntriesRef = useRef(coEntries);     // always-current snapshot for beforeunload
    coEntriesRef.current = coEntries;
    const lastLoadKeyRef = useRef(null); // Track job+version to only reload on actual job/version switch
    const bottomRef = useRef(null);

    // Load GL account valid combinations (same source as Day Zero Budget)
    useEffect(() => {
        if (selectedJob) {
            loadGLAccount({ ids: [selectedJob] }).then((data) => {
                setGlValues(data?.data || []);
            });
        }
    }, [selectedJob]);

    // Load admin-managed Resource Code table for the Resource Code dropdown
    useEffect(() => {
        if (!selectedVersion) return;
        loadResourceCodeTables({ forecastGuid: selectedVersion }).then(({ byCode }) => {
            const opts = Object.entries(byCode || {})
                .map(([code, { description }]) => ({
                    value: code,
                    label: description ? `${code} - ${description}` : code,
                }))
                .sort((a, b) => a.value.localeCompare(b.value));
            setResourceCodeOptions(opts);
        });
    }, [selectedVersion]);

    // Extract distinct Cost_Code + Cost_Type combinations from GL account data
    const getValidCombinations = () => {
        if (!glValues || !glValues.length) return [];
        const seen = new Set();
        const combos = [];
        glValues.forEach(d => {
            const cc = (d.Cost_Code || '').toString().trim();
            const ct = (d.Cost_Type || '').toString().trim();
            if (cc && ct) {
                const key = `${cc}|${ct}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    combos.push({
                        costCode: cc,
                        costType: ct,
                    });
                }
            }
        });
        return combos;
    };

    const COLORS = {
        navy: "#173659",
        navyDark: "#0F2A4A",
        headerText: "#FFFFFF",
        subHeader: "#224774",
        grid: "#B7C4D3",
        zebra: "#F6F8FB",
        rowHighlight: "#E5F3EA",
        cellText: "#0E1B2B",
        white: "#FFFFFF",
        blue: "#2563eb",
        lightBlue: "#eff6ff",
        green: "#16a34a",
        red: "#dc2626",
    };

    const bw = StyleSheet.hairlineWidth;

    const costTypeDescriptions = {
        "41000": "Revenue",
        "51100": "Regular Time",
        "51200": "Overtime",
        "51300": "Burden",
        "51400": "BYOD",
        "51500": "Per Diem",
        "51600": "Truck Pay",
        "51700": "Welding Rig Pay",
        "51800": "Field Bonus",
        "52100": "Inside Equipment Rental",
        "52200": "Outside Equipment Rental",
        "52300": "Fuel",
        "52400": "Repairs",
        "53100": "Materials",
        "53200": "Mats",
        "53300": "Tools & Supplies",
        "53400": "Skids",
        "54100": "Internal Subcontractors",
        "54200": "External Subcontractors",
        "54300": "Hauling and Transportation",
        "55100": "Personal Meals",
        "55200": "Team Meals/Entertain",
        "55300": "Airfare",
        "55400": "Lodging",
        "56100": "Field Office Rent",
        "56200": "Field Office Utility",
        "56300": "Field Office Expense",
        "56400": "Lab Fees & Testing",
        "56500": "Safety & PPE",
        "56600": "Bonds and Insurance Premiums",
        "56700": "Permits",
        "56800": "Insurance Claims",
        "57100": "Taxes",
        "57200": "Accrued Expense",
        "57300": "Warranty Reserve",
        "57900": "Miscellaneous",
        "55900": "Miscellaneous",
    };

    const statusOptions = [
        { label: 'Approved', value: 'Approved' },
        { label: 'Pending - Performed', value: 'Pending - Performed' },
        { label: 'Pending - Not Performed', value: 'Pending - Not Performed' },
        { label: 'Not Submitted', value: 'Not Submitted' },
        { label: 'Denied', value: 'Denied' },
        { label: 'Passthru', value: 'Passthru' },
    ];

    const typeOptions = [
        { label: 'Customer Requested', value: 'Customer Requested' },
        { label: 'Internal Change', value: 'Internal Change' },
        { label: 'Unit Adder', value: 'Unit Adder' },
        { label: 'Bid Miss', value: 'Bid Miss' },
    ];

    const revenueTypeOptions = [
        { label: 'Unit Price', value: 'Unit Price' },
        { label: 'T&M', value: 'T&M' },
        { label: 'Change Order', value: 'Change Order' },
        { label: 'Reduction of Scope', value: 'Reduction of Scope' },
    ];

    // Generate random 8-digit id
    const generateId = () => Math.floor(10000000 + Math.random() * 90000000).toString();

    // Create a blank CO entry
    const createBlankEntry = () => ({
        _localId: generateId(),
        coNo: '',
        description: '',
        status: 'Not Submitted',
        type: '',
        revenueType: '',
        costCode: '',
        // PSR fields
        initiated: '',
        submitted: '',
        signed: '',
        extension: '',
        task: false,
        criticalPath: false,
        notes: '',
        // Cost detail rows: array of { costType, dollars, hours, quantity, resourceCode, status }
        costLines: [{ costType: '41000', dollars: '', hours: '', quantity: '', resourceCode: '', status: '', costCode: '' }],
        _isNew: true,
        _dirty: false,
    });

    // Reset when job or version changes so data reloads with fresh jobUserData
    useEffect(() => {
        lastLoadKeyRef.current = null;
        setCoEntries([]);
        setIsLoading(true);
        // Auto-dismiss loading after 10 seconds regardless of data
        const timeout = setTimeout(() => setIsLoading(false), 10000);
        return () => clearTimeout(timeout);
    }, [selectedJob, selectedVersion]);

    // Load existing data from jobUserData - only on job/version change, NOT on every atom mutation
    useEffect(() => {
        if (jobUserData && selectedJob && selectedVersion) {
            const loadKey = `${selectedJob}_${selectedVersion}`;
            // Also verify jobUserData actually contains rows for the current job
            const hasCurrentJobData = jobUserData.some(row => row.BU_ID == selectedJob);
            if (lastLoadKeyRef.current !== loadKey && hasCurrentJobData) {
                lastLoadKeyRef.current = loadKey;
                loadEntries();
                setIsLoading(false);
            }
        }
    }, [jobUserData, selectedJob, selectedVersion]);

    const loadEntries = () => {
        try {
            // Load Change Order Header data
            const headerData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order Header" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            // Load Change Order cost rows
            const costData = jobUserData.filter(row =>
                row.Forecast_Tab === "Change Order" &&
                row.BU_ID == selectedJob &&
                row.Version_GUID == selectedVersion.toString()
            );

            const mapType = {
                "Internal C": "Internal Change",
                "Customer R": "Customer Requested",
                "Unit Adder": "Unit Adder",
                "Bid Miss": "Bid Miss",
                "Internal Change": "Internal Change",
                "Customer Requested": "Customer Requested",
            };

            // Group headers by CO number
            const headersByCoNo = {};
            headerData.forEach(h => {
                const coNo = h.rowLabel || h.Row_Label || '';
                if (coNo) headersByCoNo[coNo] = h;
            });

            // Group cost rows by CO number
            const costsByCoNo = {};
            costData.forEach(c => {
                const coNo = c.rowLabel || c.Row_Label || '';
                if (!costsByCoNo[coNo]) costsByCoNo[coNo] = [];
                costsByCoNo[coNo].push(c);
            });

            // Build entries from all unique CO numbers
            const allCoNos = new Set([...Object.keys(headersByCoNo), ...Object.keys(costsByCoNo)]);
            const entries = [];

            allCoNos.forEach(coNo => {
                const header = headersByCoNo[coNo];
                const costs = costsByCoNo[coNo] || [];

                const costLines = costs.map(c => ({
                    costType: c.costType || c.Cost_Type || '',
                    dollars: c.valueAmount || c.Value_Amount || '',
                    hours: c.hours || c.Hours || '',
                    quantity: c.quantity || c.Quantity || '',
                    resourceCode: c.resourceCode || c.Resource_Code || '',
                    fieldName: c.forecastFieldName || c.Forecast_Field_Name || '',
                    status: c.status || c.Status || '',
                    costCode: c.costCode || c.Cost_Code || '',
                    _dbGuid: c.Forecast_User_Data_GUID || c.forecastUserDataGuid || '',
                    _dbRowId: c.Forecast_Row_ID || c.forecastRowId || c.rowId || '',
                }));

                if (costLines.length === 0) {
                    costLines.push({ costType: '41000', dollars: '', hours: '', quantity: '', resourceCode: '', status: '', costCode: '' });
                }

                entries.push({
                    _localId: generateId(),
                    coNo: coNo,
                    description: header?.description || header?.Description || '',
                    status: header?.status || header?.Status || costs[0]?.status || costs[0]?.Status || '',
                    type: mapType[header?.PO_Number] || header?.PO_Number || '',
                    revenueType: header?.resourceCode || header?.Resource_Code || '',
                    costCode: header?.costCode || header?.Cost_Code || '',
                    initiated: header?.projectStart || header?.Project_Start || '',
                    submitted: header?.projectEnd || header?.Project_End || '',
                    signed: header?.phaseStart || header?.Phase_Start || '',
                    extension: header?.valueAmount || header?.Value_Amount || '',
                    task: header?.active || header?.Active || false,
                    criticalPath: header?.equipmentTier || false,
                    notes: header?.notes || header?.Notes || '',
                    costLines: costLines,
                    _isNew: false,
                    _dirty: false,
                    _headerDbGuid: header?.Forecast_User_Data_GUID || header?.forecastUserDataGuid || '',
                    _headerDbRowId: header?.Forecast_Row_ID || header?.forecastRowId || header?.rowId || '',
                });
            });

            // Sort by CO number (alphanumeric: int7 before int14)
            entries.sort((a, b) => {
                return (a.coNo || '').localeCompare(b.coNo || '', undefined, { numeric: true, sensitivity: 'base' });
            });

            setCoEntries(entries);

            // Load persisted signature-verification results (one field-row per CO).
            const sigData = jobUserData.filter(row =>
                (row.Forecast_Tab || row.tab) === SIG_TAB &&
                row.BU_ID == selectedJob &&
                (row.Version_GUID || row.version) === selectedVersion.toString()
            );
            const sigMap = {};
            sigData.forEach(row => {
                const coNo = row.Row_Label || row.rowLabel || '';
                if (!coNo) return;
                const raw = row.Notes || row.notes || '';
                try {
                    sigMap[coNo] = raw ? JSON.parse(raw) : { status: row.Forecast_Value || row.value || '' };
                } catch {
                    sigMap[coNo] = { status: row.Forecast_Value || row.value || '' };
                }
            });
            setSigResults(sigMap);
        } catch (error) {
            console.error('Error loading CO entries:', error);
        }
    };

    const updateEntry = (localId, field, value) => {
        setCoEntries(prev => {
            const updated = prev.map(e => {
                if (e._localId !== localId) return e;
                const patch = { ...e, [field]: value, _dirty: true, _headerDirty: true };
                // When status changes, cascade the new value to every cost line so
                // the header status is the single source of truth (other pages read
                // status off the line rows).
                if (field === 'status') {
                    patch.costLines = e.costLines.map(l => ({ ...l, status: value, _lineDirty: true }));
                }
                // When the CO number changes, every line row's Row_Label must be
                // rewritten too — otherwise the header renames but the cost lines
                // stay under the old number (orphaned lines, CO shows $0).
                if (field === 'coNo') {
                    if (patch._prevCoNo === undefined) patch._prevCoNo = e.coNo;
                    patch.costLines = e.costLines.map(l => ({ ...l, _lineDirty: true }));
                }
                return patch;
            });
            // Schedule auto-save for this entry
            scheduleAutoSave(localId, updated);
            return updated;
        });
    };

    const updateCostLine = (localId, lineIdx, field, value) => {
        setCoEntries(prev => {
            const updated = prev.map(e => {
                if (e._localId !== localId) return e;
                const newLines = [...e.costLines];
                newLines[lineIdx] = { ...newLines[lineIdx], [field]: value, _lineDirty: true };
                return { ...e, costLines: newLines, _dirty: true };
            });
            scheduleAutoSave(localId, updated);
            return updated;
        });
    };

    const addCostLine = (localId) => {
        setCoEntries(prev => prev.map(e => {
            if (e._localId !== localId) return e;
            return {
                ...e,
                costLines: [...e.costLines, { costType: '', dollars: '', hours: '', quantity: '', resourceCode: '', status: '', costCode: '' }],
                _dirty: true
            };
        }));
    };

    const addCostLineWithValues = (localId, initialValues) => {
        setCoEntries(prev => {
            const updated = prev.map(e => {
                if (e._localId !== localId) return e;
                return {
                    ...e,
                    costLines: [...e.costLines, { costType: '', dollars: '', hours: '', quantity: '', resourceCode: '', status: '', costCode: '', ...initialValues, _lineDirty: true }],
                    _dirty: true
                };
            });
            scheduleAutoSave(localId, updated);
            return updated;
        });
    };

    const removeCostLine = (localId, lineIdx) => {
        setCoEntries(prev => prev.map(e => {
            if (e._localId !== localId) return e;
            const removedLine = e.costLines[lineIdx];
            // If the removed line has a DB GUID, delete it from the database
            if (removedLine && removedLine._dbGuid) {
                deleteUserData({
                    id: selectedJob,
                    forecastGuid: selectedVersion,
                    lockedVersion: lockedVersion,
                    rowId: removedLine._dbGuid + '',
                    tab: 'Change Order'
                }).catch(err => console.warn('Error deleting cost line from DB:', err));
            }
            const newLines = e.costLines.filter((_, i) => i !== lineIdx);
            return { ...e, costLines: newLines.length > 0 ? newLines : [{ costCode: '', costType: '', dollars: '', hours: '', quantity: '', resourceCode: '' }], _dirty: true };
        }));
    };

    const addNewEntry = () => {
        const newEntry = createBlankEntry();
        setCoEntries(prev => [...prev, newEntry]);
        // Auto-expand newly added cards
        setExpandedCards(prev => ({ ...prev, [newEntry._localId]: true }));
        // Scroll to bottom after render so the new card is visible
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    // Auto-save: debounce 1.5s after last edit
    const scheduleAutoSave = useCallback((localId, entries) => {
        if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(async () => {
            const entry = entries.find(e => e._localId === localId);
            if (entry && entry._dirty && entry.coNo) {
                await saveEntry(entry);
            }
        }, 1500);
    }, [selectedJob, selectedVersion, lockedVersion, userId, userName]);

    // Cleanup timer on unmount + flush pending writes
    useEffect(() => {
        const handleBeforeUnload = () => {
            // Force-save any dirty entries via beacon (best effort)
            const dirty = coEntriesRef.current.filter(e => e._dirty && e.coNo);
            if (dirty.length > 0) {
                dirty.forEach(entry => {
                    // Trigger save so rows enter the write queue
                    saveEntry(entry);
                });
            }
            flushWriteQueue();
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
            // On unmount (tab switch), save dirty entries and flush queue
            const dirty = coEntriesRef.current.filter(e => e._dirty && e.coNo);
            dirty.forEach(entry => saveEntry(entry));
            flushWriteQueue();
        };
    }, []);

    const removeEntry = async (localId) => {
        const entry = coEntries.find(e => e._localId === localId);
        if (!entry) return;

        if (!entry._isNew && entry.coNo) {
            // Delete from database - delete rows matching this CO number
            try {
                await deleteCOByRowLabel({
                    id: selectedJob,
                    forecastGuid: selectedVersion.toString(),
                    rowLabel: entry.coNo.toString()
                });
            } catch (err) {
                console.error('Error deleting CO from database:', err);
            }
        }

        setShowDeleteConfirm(null);
        setCoEntries(prev => prev.filter(e => e._localId !== localId));
    };

    /**
     * Save a single CO entry → multiple DB rows
     * 
     * Creates:
     *   1. A "Change Order Header" row with metadata
     *   2. One "Change Order" row per cost line, each with shared metadata (status, etc.)
     */
    const saveEntry = async (entry) => {
        if (!entry.coNo) {
            alert('CO Number is required');
            return;
        }

        // Prevent concurrent saves — queue if one is already in-flight
        if (saveInProgressRef.current) {
            pendingSaveEntryRef.current = entry;
            return;
        }
        saveInProgressRef.current = true;

        setIsSaving(true);
        try {
            const rowsToSave = [];
            const coNumber = entry.coNo.toString();

            // Map type to short form for PO_Number field
            const typeMap = {
                "Internal Change": "Internal C",
                "Customer Requested": "Customer R",
                "Unit Adder": "Unit Adder",
                "Bid Miss": "Bid Miss",
            };

            // Map revenue type to fieldName
            const revenueFieldMap = {
                'Unit Price': 'unitPrice',
                'T&M': 'tAndM',
                'Change Order': 'changeOrders',
                'Reduction of Scope': 'reductionOfScope',
            };

            // 1. Create header row — reuse existing DB GUID if available (update),
            //    reuse saved rowId if previously saved this session (EXISTS→update),
            //    otherwise generate a new rowId and insert.
            const isHeaderUpdate = !entry._isNew && entry._headerDbGuid;
            const isHeaderSavedNoGuid = !entry._isNew && !entry._headerDbGuid && entry._headerDbRowId;
            const headerRowId = isHeaderUpdate ? entry._headerDbRowId : isHeaderSavedNoGuid ? entry._headerDbRowId : `${coNumber}_header_${generateId()}`;
            const headerRow = {
                userId: userId || '154045',
                userName: userName || 'Seth Bailey',
                tab: "Change Order Header",
                rowId: headerRowId,
                version: selectedVersion + '',
                id: selectedJob,
                lockedVersion: lockedVersion,
                fieldName: 'Row_Label',
                value: coNumber,
                valueAmount: isNaN(pNum(entry.extension)) ? 0 : pNum(entry.extension),
                costCode: entry.costCode || '',
                rowLabel: coNumber,
                description: entry.description || '',
                status: entry.status || '',
                notes: entry.notes || '',
                po: typeMap[entry.type] || entry.type || '',
                quantity: '', // Days Ext not used here
                resourceCode: entry.revenueType || '',
                active: entry.task || false,
                projectStart: entry.initiated || '',
                projectEnd: entry.submitted || '',
                phaseStart: entry.signed || '',
                equipmentTier: entry.criticalPath || false,
                ...(isHeaderUpdate ? { updateRow: 1, guidId: entry._headerDbGuid + '' } : isHeaderSavedNoGuid ? {} : { insertRow: 1 }),
            };
            rowsToSave.push(headerRow);

            // 2. Create cost line rows — track mapping from costLine index to generated rowId
            const lineRowIdMap = {};
            entry.costLines.forEach((line, idx) => {
                if (!line.costType && !line.dollars && !line.hours && !line.quantity) return; // Skip empty lines

                const costTypeStr = line.costType ? String(line.costType).trim() : '';
                const costType = costTypeStr.includes(' ') ? costTypeStr.split(' ')[0] : costTypeStr;

                // Determine fieldName
                let fieldName = 'row';
                if (costType === '41000') {
                    fieldName = revenueFieldMap[line.resourceCode] || revenueFieldMap[entry.revenueType] || 'changeOrders';
                } else if (costType === '51100' && line.hours && pNum(line.hours) !== 0) {
                    fieldName = 'totalLaborHours';
                } else if (costType === '52100' && line.hours && pNum(line.hours) !== 0) {
                    fieldName = 'totalEquipmentHours';
                } else if (costType && costType.match(/^5\d{4}$/)) {
                    fieldName = costType;
                }

                const isLineUpdate = !entry._isNew && line._dbGuid;
                const isLineSavedNoGuid = !entry._isNew && !line._dbGuid && line._dbRowId;
                const uniqueRowId = isLineUpdate ? line._dbRowId : isLineSavedNoGuid ? line._dbRowId : `${coNumber}_${fieldName}_${generateId()}`;
                lineRowIdMap[idx] = uniqueRowId;
                const processedDollars = isNaN(pNum(line.dollars)) ? 0 : pNum(line.dollars);
                const processedHours = isNaN(pNum(line.hours)) ? 0 : pNum(line.hours);
                const processedQuantity = isNaN(pNum(line.quantity)) ? 0 : pNum(line.quantity);

                const dbRow = {
                    userId: userId || '154045',
                    userName: userName || 'Seth Bailey',
                    tab: "Change Order",
                    rowId: uniqueRowId,
                    version: selectedVersion + '',
                    id: selectedJob,
                    lockedVersion: lockedVersion,
                    fieldName: fieldName,
                    value: line.dollars || '',
                    valueAmount: processedDollars,
                    costCode: line.costCode || entry.costCode || '',
                    costType: costType,
                    rowLabel: coNumber,
                    status: line.status || '', // Preserve per-line status; blank means not approved
                    resourceCode: line.resourceCode || '',
                    hours: processedHours,
                    quantity: processedQuantity,
                    description: entry.description || '',
                    notes: entry.notes || '',
                    ...(isLineUpdate ? { updateRow: 1, guidId: line._dbGuid + '' } : isLineSavedNoGuid ? {} : { insertRow: 1 }),
                };
                rowsToSave.push(dbRow);
            });

            // Only send rows that actually changed (partial save for existing DB entries).
            // A CO-number rename is ALWAYS a full save: Row_Label is the header↔line
            // join key, so every row must be rewritten together or the lines orphan.
            const wasRenamed = entry._prevCoNo !== undefined && entry._prevCoNo !== null &&
                entry._prevCoNo.toString() !== coNumber;
            const isFullSave = entry._isNew || wasRenamed || (!entry._headerDbGuid && !entry._headerDbRowId);
            let rowsToSend;
            if (isFullSave) {
                rowsToSend = rowsToSave;
            } else {
                rowsToSend = [];
                if (entry._headerDirty) rowsToSend.push(rowsToSave[0]);
                let rowIdx = 1;
                entry.costLines.forEach((line) => {
                    if (!line.costType && !line.dollars && !line.hours && !line.quantity) return;
                    if (line._lineDirty) rowsToSend.push(rowsToSave[rowIdx]);
                    rowIdx++;
                });
            }

            console.log(`Saving CO entry: ${rowsToSend.length} dirty of ${rowsToSave.length} total rows`);

            if (rowsToSend.length > 0) {
                await updateUserDataArray(rowsToSend);
            }

            // Update local jobUserData atom to stay in sync
            // Remove old rows for this CO, then add the new saved rows
            setJobUserData(prev => {
                if (!prev) return prev;
                const coNum = entry.coNo.toString();
                const prevCoNum = entry._prevCoNo !== undefined && entry._prevCoNo !== null ? entry._prevCoNo.toString() : '';
                const filtered = prev.filter(row => {
                    const label = row.Row_Label || row.rowLabel || '';
                    const tab = row.Forecast_Tab || row.tab || '';
                    const bu = (row.BU_ID || row.id || '').toString();
                    const ver = (row.Version_GUID || row.version || '').toString();
                    const isThisCO = (label === coNum || (prevCoNum && label === prevCoNum)) &&
                        bu == selectedJob &&
                        ver == selectedVersion.toString() &&
                        (tab === 'Change Order Header' || tab === 'Change Order');
                    return !isThisCO;
                });
                const newAtomRows = rowsToSave.map(r => ({
                    ...r,
                    Forecast_Tab: r.tab,
                    Forecast_Row_ID: r.rowId,
                    BU_ID: r.id,
                    Version_GUID: r.version,
                    Row_Label: r.rowLabel,
                    Value_Amount: r.valueAmount,
                    Cost_Type: r.costType,
                    Cost_Code: r.costCode,
                    Resource_Code: r.resourceCode,
                    PO_Number: r.po,
                    Forecast_Field_Name: r.fieldName,
                    Description: r.description,
                    Status: r.status,
                    Notes: r.notes,
                    Hours: r.hours,
                    Quantity: r.quantity,
                }));
                return [...filtered, ...newAtomRows];
            });

            // Mark as saved — clear dirty flags and store generated rowIds so
            // subsequent saves reuse the same rowId (server EXISTS→update, no duplicates)
            setCoEntries(prev => prev.map(e => {
                if (e._localId !== entry._localId) return e;
                return {
                    ...e,
                    _dirty: false,
                    _isNew: false,
                    _headerDirty: false,
                    _prevCoNo: undefined,
                    _headerDbRowId: headerRowId,
                    costLines: e.costLines.map((l, idx) => ({
                        ...l,
                        _lineDirty: false,
                        ...(lineRowIdMap[idx] ? { _dbRowId: lineRowIdMap[idx] } : {}),
                    }))
                };
            }));

            console.log(`Saved CO #${coNumber}: ${rowsToSave.length} rows (1 header + ${rowsToSave.length - 1} cost lines)`);

            // Trigger signature verification when a Signed date was just added or
            // changed on a customer-facing CO. Fire-and-forget — the ~6s vision
            // call manages its own spinner/result state and must not block the save.
            const lastVerifiedSigned = sigResults[coNumber]?.signedDate || '';
            if (entry.signed && entry.signed !== lastVerifiedSigned) {
                runAndPersistVerification(coNumber, entry.signed, entry.type, entry.description);
            }
        } catch (error) {
            console.error('Error saving CO entry:', error);
            alert('Error saving change order. Please try again.');
        } finally {
            setIsSaving(false);
            saveInProgressRef.current = false;
            // If another save was queued while we were saving, execute it now
            if (pendingSaveEntryRef.current) {
                const pending = pendingSaveEntryRef.current;
                pendingSaveEntryRef.current = null;
                saveEntry(pending);
            }
        }
    };

    // ─── Upload handler (same as cologUpload) ───
    const fileSelectedHandler = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const sheetNames = wb.SheetNames;
                const generateRandomId = () => Math.floor(10000000 + Math.random() * 90000000).toString();
                let rowsToSave = [];

                // Process Headers sheet
                if (sheetNames.includes('Headers')) {
                    const headersSheet = wb.Sheets['Headers'];
                    const sheetData = XLSX.utils.sheet_to_json(headersSheet, { header: 1, raw: true });
                    if (sheetData && sheetData.length > 0) {
                        const headerColumns = sheetData[1] || [];
                        const dateFields = ['Initiated', 'Submitted', 'Signed'];
                        const validateCheckbox = (v) => v === true || v === 1 || v === 'TRUE' || v === 'true' || v === 'Yes' || v === 'yes' || v === 'X' || v === 'x';
                        const formatExcelDate = (v) => { if (typeof v === 'number' && v > 25000 && v < 100000) { const ep = new Date(1899, 11, 30); return new Date(ep.getTime() + v * 86400000).toLocaleDateString('en-US'); } return v; };
                        // PO_Number column is 10 chars, so map full names down to match manual saves
                        const shortenType = {
                            'Internal Change': 'Internal C',
                            'Customer Requested': 'Customer R',
                            'Unit Adder': 'Unit Adder',
                            'Bid Miss': 'Bid Miss',
                        };
                        sheetData.slice(2).forEach(row => {
                            if (row.length > 0 && row.some(c => c != null && c !== '')) {
                                const rd = {};
                                headerColumns.forEach((col, ci) => { if (col && row[ci] != null) rd[col] = row[ci]; });
                                if (rd['No.'] != null && rd['No.'] !== '') {
                                    const coNumber = rd['No.'] + '';
                                    dateFields.forEach(f => { if (rd[f]) rd[f] = formatExcelDate(rd[f]); });
                                    const rawType = rd['Type'] || '';
                                    const poValue = shortenType[rawType] || rawType;
                                    rowsToSave.push({
                                        userId: userId || '154045', userName: userName || '', tab: 'Change Order Header',
                                        rowId: coNumber + '_' + generateRandomId(), version: selectedVersion + '', id: selectedJob,
                                        lockedVersion, fieldName: 'Row_Label', value: coNumber,
                                        valueAmount: isNaN(pNum(rd['Extension'])) ? 0 : pNum(rd['Extension']),
                                        costCode: rd['Phase'] || rd['Cost Code'] || '', rowLabel: coNumber,
                                        description: rd['Description'] || '', status: rd['Status'] || '',
                                        notes: rd['Notes'] || '', po: poValue, quantity: rd['Days Ext'] || '',
                                        resourceCode: rd['Revenue Type'] || '', active: validateCheckbox(rd['Task']),
                                        projectStart: rd['Initiated'] || '', projectEnd: rd['Submitted'] || '',
                                        phaseStart: rd['Signed'] || '', equipmentTier: validateCheckbox(rd['Critical Path']),
                                    });
                                }
                            }
                        });
                    }
                }

                // Process Upload sheet
                if (wb.Sheets['Upload']) {
                    const jsonData = XLSX.utils.sheet_to_json(wb.Sheets['Upload']);
                    const normalizedData = jsonData.map(row => {
                        const nr = {}; Object.keys(row).forEach(k => { nr[k.trim()] = row[k]; }); return nr;
                    });
                    normalizedData.forEach(row => {
                        const coId = row['CO ID'] != null ? String(row['CO ID']) : '';
                        if (!coId) return;
                        const ctStr = row['Cost Type'] ? String(row['Cost Type']).substring(0, 5).trim() : '';
                        const rc = row['Resource Code'] || '';
                        let fieldName = 'row';
                        if (ctStr === '41000') {
                            if (rc.toLowerCase().includes('t&m') || rc.toLowerCase().includes('tm')) fieldName = 'tAndM';
                            else if (rc.toLowerCase().includes('change') || rc.toLowerCase().includes('co')) fieldName = 'changeOrders';
                            else if (rc.toLowerCase().includes('reduction') || rc.toLowerCase().includes('ros')) fieldName = 'reductionOfScope';
                            else if (rc.toLowerCase().includes('unit') || rc.toLowerCase().includes('price')) fieldName = 'unitPrice';
                            else fieldName = 'changeOrders';
                        } else if (ctStr === '51100' && row['Hours'] && pNum(row['Hours']) !== 0) fieldName = 'totalLaborHours';
                        else if (ctStr === '52100' && row['Hours'] && pNum(row['Hours']) !== 0) fieldName = 'totalEquipmentHours';
                        else if (ctStr.length === 5 && ctStr.startsWith('5')) fieldName = ctStr;
                        rowsToSave.push({
                            userId: userId || '154045', userName: userName || '', tab: 'Change Order',
                            rowId: coId + '_' + fieldName + '_' + generateRandomId(), version: selectedVersion + '', id: selectedJob,
                            lockedVersion, fieldName, value: row['Dollars'] || '',
                            valueAmount: isNaN(pNum(row['Dollars'])) ? 0 : pNum(row['Dollars']),
                            costCode: row['Cost Code'] ? String(row['Cost Code']).substring(0, 6) : '',
                            costType: ctStr, rowLabel: coId, status: row['Status'] || '',
                            resourceCode: rc, hours: isNaN(pNum(row['Hours'])) ? 0 : pNum(row['Hours']),
                            quantity: isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']),
                            description: '', notes: '',
                        });
                    });
                }

                if (rowsToSave.length > 0) {
                    const uploadedHeaderCount = rowsToSave.filter(r => r.tab === 'Change Order Header').length;
                    const existingHeaderCount = (jobUserData || []).filter(r =>
                        r.Forecast_Tab === 'Change Order Header' &&
                        r.BU_ID == selectedJob &&
                        r.Version_GUID == selectedVersion.toString()
                    ).length;
                    if (uploadedHeaderCount === 0 && existingHeaderCount > 0) {
                        const proceed = window.confirm(
                            `The Excel file has no Change Order header rows, but ${existingHeaderCount} header row${existingHeaderCount === 1 ? '' : 's'} already exist${existingHeaderCount === 1 ? 's' : ''} in the PSR view. Continuing will delete the existing headers. Proceed?`
                        );
                        if (!proceed) { setIsUploading(false); return; }
                    }
                    await deleteCO({ id: selectedJob, forecastGuid: selectedVersion.toString() });
                    await updateUserDataArray(rowsToSave);
                    alert(`Uploaded ${rowsToSave.length} rows successfully. Reloading data...`);
                    // Trigger a reload by re-reading entries
                    window.location.reload();
                } else {
                    alert('No valid data found in the Excel file.');
                }
            } catch (error) {
                console.error('Error processing Excel file:', error);
                alert('Error processing Excel file: ' + error.message);
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    // ─── V2 Export handler (Lines + PSR Lines template) ───
    const exportCOLogsToExcelV2 = async () => {
        const filename = `colog_v2_${selectedJob}.xlsx`;
        setIsExporting(true);
        try {
            const changeOrderData = jobUserData.filter(row =>
                row.Forecast_Tab === 'Change Order' && row.BU_ID == selectedJob && row.Version_GUID == selectedVersion.toString()
            );
            const changeOrderHeaderData = jobUserData.filter(row =>
                row.Forecast_Tab === 'Change Order Header' && row.BU_ID == selectedJob && row.Version_GUID == selectedVersion.toString()
            );
            // Lines data (cost detail rows)
            const linesData = changeOrderData.map(row => ({
                'CO ID': row.rowLabel || row.Row_Label || '', 'Status': row.status || row.Status || '',
                'Cost Code': row.costCode || row.Cost_Code || '', 'Cost Type': row.costType || row.Cost_Type || '',
                'Dollars': row.valueAmount || row.Value_Amount || 0, 'Hours': row.hours || row.Hours || 0,
                'Resource Code': row.resourceCode || row.Resource_Code || '', 'Quantity': row.quantity || row.Quantity || 0,
            }));
            // PSR Lines data (header-level rows)
            const expandType = {
                'Internal C': 'Internal Change',
                'Customer R': 'Customer Requested',
                'Unit Adder': 'Unit Adder',
                'Bid Miss': 'Bid Miss',
            };
            const psrLinesData = changeOrderHeaderData.map(row => {
                const fmtDate = (v) => { if (!v) return ''; const d = moment(v); return d.isValid() ? d.format('MM/DD/YYYY') : ''; };
                const rawPo = row.PO_Number || '';
                return {
                    'No.': row.rowLabel || row.Row_Label || '', 'Type': expandType[rawPo] || rawPo,
                    'Revenue Type': row.resourceCode || row.Resource_Code || '',
                    'Description': row.description || row.Description || '', 'Status': row.status || row.Status || '',
                    'Initiated': fmtDate(row.projectStart || row.Project_Start),
                    'Submitted': fmtDate(row.projectEnd || row.Project_End),
                    'Signed': fmtDate(row.phaseStart || row.Phase_Start),
                    'Extension': row.valueAmount || row.Value_Amount || 0,
                    'Task': row.active || row.Active || false, 'Critical Path': row.equipmentTier || false,
                    'Notes': row.notes || row.Notes || '',
                };
            });
            const sortByCoId = (a, b) => (parseFloat(a['No.'] || a['CO ID']) || 0) - (parseFloat(b['No.'] || b['CO ID']) || 0);
            psrLinesData.sort(sortByCoId); linesData.sort(sortByCoId);

            const pcodes = jobPhaseCodes ? Object.keys(jobPhaseCodes).filter(c => /^\d{6}$/.test(c)).sort() : [];
            const phaseCodesWithDescriptions = pcodes.map(c => `${c} - ${jobPhaseCodes[c]}`);
            const ctDescs = costTypeDescriptions;
            const ctKeys = Object.keys(ctDescs).filter(c => c === '41000' || c.startsWith('5')).sort();
            const costTypesWithDescriptions = ctKeys.map(c => `${c} - ${ctDescs[c]}`);

            const response = await fetch('https://forecastingtestpythonservice.azurewebsites.net/api/download_co_template_v2', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    coRows: linesData,
                    psrLines: psrLinesData,
                    jobId: selectedJob,
                    jobData: { BU_Description: selectedJob },
                    phaseCodes: phaseCodesWithDescriptions,
                    costTypes: costTypesWithDescriptions,
                    validCombinations: getValidCombinations(),
                }),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading CO Log V2 Excel file:', error);
            alert('Failed to download CO Log V2 Excel file. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // ─── V2 Upload handler (reads "Lines" + "PSR Lines" tabs) ───
    const fileSelectedHandlerV2 = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const sheetNames = wb.SheetNames;
                const generateRandomId = () => Math.floor(10000000 + Math.random() * 90000000).toString();
                let rowsToSave = [];

                // Process "PSR Lines" sheet (header rows)
                if (sheetNames.includes('PSR Lines')) {
                    const psrSheet = wb.Sheets['PSR Lines'];
                    const sheetData = XLSX.utils.sheet_to_json(psrSheet, { header: 1, raw: true });
                    if (sheetData && sheetData.length > 0) {
                        const headerColumns = sheetData[1] || [];
                        const dateFields = ['Initiated', 'Submitted', 'Signed'];
                        const validateCheckbox = (v) => v === true || v === 1 || v === 'TRUE' || v === 'true' || v === 'Yes' || v === 'yes' || v === 'X' || v === 'x';
                        const formatExcelDate = (v) => { if (typeof v === 'number' && v > 25000 && v < 100000) { const ep = new Date(1899, 11, 30); return new Date(ep.getTime() + v * 86400000).toLocaleDateString('en-US'); } return v; };
                        // PO_Number column is 10 chars, so map full names down to match manual saves
                        const shortenType = {
                            'Internal Change': 'Internal C',
                            'Customer Requested': 'Customer R',
                            'Unit Adder': 'Unit Adder',
                            'Bid Miss': 'Bid Miss',
                        };
                        sheetData.slice(2).forEach(row => {
                            if (row.length > 0 && row.some(c => c != null && c !== '')) {
                                const rd = {};
                                headerColumns.forEach((col, ci) => { if (col && row[ci] != null) rd[col] = row[ci]; });
                                if (rd['No.'] != null && rd['No.'] !== '') {
                                    const coNumber = rd['No.'] + '';
                                    dateFields.forEach(f => { if (rd[f]) rd[f] = formatExcelDate(rd[f]); });
                                    const rawType = rd['Type'] || '';
                                    const poValue = shortenType[rawType] || rawType;
                                    rowsToSave.push({
                                        userId: userId || '154045', userName: userName || '', tab: 'Change Order Header',
                                        rowId: coNumber + '_' + generateRandomId(), version: selectedVersion + '', id: selectedJob,
                                        lockedVersion, fieldName: 'Row_Label', value: coNumber,
                                        valueAmount: isNaN(pNum(rd['Extension'])) ? 0 : pNum(rd['Extension']),
                                        costCode: rd['Phase'] || rd['Cost Code'] || '', rowLabel: coNumber,
                                        description: rd['Description'] || '', status: rd['Status'] || '',
                                        notes: rd['Notes'] || '', po: poValue, quantity: rd['Days Ext'] || '',
                                        resourceCode: rd['Revenue Type'] || '', active: validateCheckbox(rd['Task']),
                                        projectStart: rd['Initiated'] || '', projectEnd: rd['Submitted'] || '',
                                        phaseStart: rd['Signed'] || '', equipmentTier: validateCheckbox(rd['Critical Path']),
                                    });
                                }
                            }
                        });
                    }
                }

                // Process "Lines" sheet (cost detail rows)
                if (wb.Sheets['Lines']) {
                    const jsonData = XLSX.utils.sheet_to_json(wb.Sheets['Lines']);
                    const normalizedData = jsonData.map(row => {
                        const nr = {}; Object.keys(row).forEach(k => { nr[k.trim()] = row[k]; }); return nr;
                    });
                    normalizedData.forEach(row => {
                        const coId = row['CO ID'] != null ? String(row['CO ID']) : '';
                        if (!coId) return;
                        const ctStr = row['Cost Type'] ? String(row['Cost Type']).substring(0, 5).trim() : '';
                        const rc = row['Resource Code'] || '';
                        let fieldName = 'row';
                        if (ctStr === '41000') {
                            if (rc.toLowerCase().includes('t&m') || rc.toLowerCase().includes('tm')) fieldName = 'tAndM';
                            else if (rc.toLowerCase().includes('change') || rc.toLowerCase().includes('co')) fieldName = 'changeOrders';
                            else if (rc.toLowerCase().includes('reduction') || rc.toLowerCase().includes('ros')) fieldName = 'reductionOfScope';
                            else if (rc.toLowerCase().includes('unit') || rc.toLowerCase().includes('price')) fieldName = 'unitPrice';
                            else fieldName = 'changeOrders';
                        } else if (ctStr === '51100' && row['Hours'] && pNum(row['Hours']) !== 0) fieldName = 'totalLaborHours';
                        else if (ctStr === '52100' && row['Hours'] && pNum(row['Hours']) !== 0) fieldName = 'totalEquipmentHours';
                        else if (ctStr.length === 5 && ctStr.startsWith('5')) fieldName = ctStr;
                        rowsToSave.push({
                            userId: userId || '154045', userName: userName || '', tab: 'Change Order',
                            rowId: coId + '_' + fieldName + '_' + generateRandomId(), version: selectedVersion + '', id: selectedJob,
                            lockedVersion, fieldName, value: row['Dollars'] || '',
                            valueAmount: isNaN(pNum(row['Dollars'])) ? 0 : pNum(row['Dollars']),
                            costCode: row['Cost Code'] ? String(row['Cost Code']).substring(0, 6) : '',
                            costType: ctStr, rowLabel: coId, status: row['Status'] || '',
                            resourceCode: rc, hours: isNaN(pNum(row['Hours'])) ? 0 : pNum(row['Hours']),
                            quantity: isNaN(pNum(row['Quantity'])) ? 0 : pNum(row['Quantity']),
                            description: '', notes: '',
                        });
                    });
                }

                if (rowsToSave.length > 0) {
                    const uploadedHeaderCount = rowsToSave.filter(r => r.tab === 'Change Order Header').length;
                    const existingHeaderCount = (jobUserData || []).filter(r =>
                        r.Forecast_Tab === 'Change Order Header' &&
                        r.BU_ID == selectedJob &&
                        r.Version_GUID == selectedVersion.toString()
                    ).length;
                    if (uploadedHeaderCount === 0 && existingHeaderCount > 0) {
                        const proceed = window.confirm(
                            `The Excel file has no PSR Lines (header) rows, but ${existingHeaderCount} header row${existingHeaderCount === 1 ? '' : 's'} already exist${existingHeaderCount === 1 ? 's' : ''} in the PSR view. Continuing will delete the existing headers. Proceed?`
                        );
                        if (!proceed) { setIsUploading(false); return; }
                    }
                    await deleteCO({ id: selectedJob, forecastGuid: selectedVersion.toString() });
                    await updateUserDataArray(rowsToSave);
                    alert(`V2 Upload: ${rowsToSave.length} rows imported successfully. Reloading data...`);
                    window.location.reload();
                } else {
                    alert('No valid data found in the V2 Excel file. Expected "Lines" and/or "PSR Lines" tabs.');
                }
            } catch (error) {
                console.error('Error processing V2 Excel file:', error);
                alert('Error processing V2 Excel file: ' + error.message);
            } finally {
                setIsUploading(false);
            }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const d = moment(dateStr);
        return d.isValid() ? d.format('YYYY-MM-DD') : dateStr;
    };

    const getPhaseDescription = (code) => {
        if (!code) return '';
        return jobPhaseCodes && jobPhaseCodes[code] ? `${code} - ${jobPhaseCodes[code]}` : code;
    };

    const calcRevenue = (entry) => {
        return entry.costLines
            .filter(l => l.costType === '41000' || l.costType === '41000 - Revenue')
            .reduce((sum, l) => sum + (isNaN(pNum(l.dollars)) ? 0 : pNum(l.dollars)), 0);
    };

    // Signed on purpose: credit/write-off COs carry negative cost lines, and
    // absolute value would flip them into a cost and double the profit error.
    const calcCost = (entry) => {
        return entry.costLines
            .filter(l => {
                const ct = l.costType ? String(l.costType).split(' ')[0] : '';
                return ct.startsWith('5');
            })
            .reduce((sum, l) => sum + (isNaN(pNum(l.dollars)) ? 0 : pNum(l.dollars)), 0);
    };

    // Prepare phase codes for dropdown
    const phaseCodes = jobPhaseCodes ? Object.keys(jobPhaseCodes).filter(code => /^\d{6}$/.test(code)).sort() : [];

    const inputStyle = {
        borderWidth: 1,
        borderColor: '#d1d5db',
        borderRadius: 4,
        padding: '4px 8px',
        fontSize: 13,
        color: '#1e293b',
        backgroundColor: '#fff',
        outline: 'none',
        minHeight: 30,
    };

    const selectStyle = {
        ...inputStyle,
        cursor: 'pointer',
    };

    const labelStyle = {
        fontSize: 11,
        fontWeight: '600',
        color: '#475569',
        marginBottom: 2,
    };

    return (
        <View style={{ backgroundColor: '#fff', maxWidth: 1500 }}>
            {/* Toolbar */}
            <View style={{ backgroundColor: '#fff', paddingLeft: 20, flexDirection: 'row', paddingTop: 8, paddingBottom: 8, gap: 10, alignItems: 'center' }}>
                <Pressable
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#2563eb',
                        padding: 10,
                        paddingHorizontal: 16,
                        borderRadius: 6,
                        flexDirection: 'row',
                    }}
                    onPress={addNewEntry}
                >
                    <Icon3 name="MdAdd" size={16} color="#fff" />
                    <Text style={{ color: '#fff', fontWeight: '600', fontSize: 13, marginLeft: 4 }}>
                        Add Change Order
                    </Text>
                </Pressable>

                {/* <Pressable
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#f8fafc',
                        padding: 10,
                        paddingHorizontal: 16,
                        borderRadius: 6,
                        flexDirection: 'row',
                        border: '1px solid #cbd5e1',
                        opacity: isUploading ? 0.6 : 1,
                    }}
                    onPress={() => !isUploading && fileInputRef.current.click()}
                    disabled={isUploading}
                >
                    <Icon3 name="MdUploadFile" size={16} color="#334155" />
                    <Text style={{ color: '#334155', fontWeight: '500', fontSize: 13, marginLeft: 4 }}>
                        {isUploading ? 'Processing...' : 'Upload Excel'}
                    </Text>
                </Pressable>

                <Pressable
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#f8fafc',
                        padding: 10,
                        paddingHorizontal: 16,
                        borderRadius: 6,
                        flexDirection: 'row',
                        border: '1px solid #cbd5e1',
                        opacity: isExporting ? 0.6 : 1,
                    }}
                    onPress={() => !isExporting && exportCOLogsToExcel()}
                    disabled={isExporting}
                >
                    <Icon3 name="MdDownload" size={16} color="#334155" />
                    <Text style={{ color: '#334155', fontWeight: '500', fontSize: 13, marginLeft: 4 }}>
                        {isExporting ? 'Exporting...' : 'Export Excel'}
                    </Text>
                </Pressable> */}

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none', height: 0, width: 0 }}
                    onChange={fileSelectedHandler}
                    accept=".xlsx"
                />

                {/* V2 Upload Button */}
                <Pressable
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#e0f2fe',
                        padding: 10,
                        paddingHorizontal: 16,
                        borderRadius: 6,
                        flexDirection: 'row',
                        border: '1px solid #7dd3fc',
                        opacity: isUploading ? 0.6 : 1,
                    }}
                    onPress={() => !isUploading && fileInputV2Ref.current && fileInputV2Ref.current.click()}
                    disabled={isUploading}
                >
                    <Icon3 name="MdUploadFile" size={16} color="#0369a1" />
                    <Text style={{ color: '#0369a1', fontWeight: '500', fontSize: 13, marginLeft: 4 }}>
                        {isUploading ? 'Uploading Excel...' : 'Upload Excel'}
                    </Text>
                </Pressable>

                {/* V2 Export Button */}
                <Pressable
                    style={{
                        alignItems: 'center',
                        backgroundColor: '#e0f2fe',
                        padding: 10,
                        paddingHorizontal: 16,
                        borderRadius: 6,
                        flexDirection: 'row',
                        border: '1px solid #7dd3fc',
                        opacity: isExporting ? 0.6 : 1,
                    }}
                    onPress={() => !isExporting && exportCOLogsToExcelV2()}
                    disabled={isExporting}
                >
                    <Icon3 name="MdDownload" size={16} color="#0369a1" />
                    <Text style={{ color: '#0369a1', fontWeight: '500', fontSize: 13, marginLeft: 4 }}>
                        {isExporting ? 'Exporting Excel...' : 'Export Excel'}
                    </Text>
                </Pressable>

                <input
                    type="file"
                    ref={fileInputV2Ref}
                    style={{ display: 'none', height: 0, width: 0 }}
                    onChange={fileSelectedHandlerV2}
                    accept=".xlsx"
                />

                {isSaving && (
                    <Text style={{ fontSize: 12, color: '#d97706', fontWeight: '500' }}>Auto-saving...</Text>
                )}
            </View>

            {/* Value bar: Bid Budget + Approved CO's = Current Budget — named and computed
                to match the Bid Forecast column headers (approved 41000 revenue only, so the
                total ties out to that tab's Current Budget). Pending CO revenue shows as a
                caption. The contract-doc indicator (03.01 - Contract in SharePoint) sits on
                the budget figure. */}
            {(() => {
                const totalBudget = smBudgets?.contractValuesRevised || 0;
                const coRev = coEntries.reduce((s, e) => s + calcRevenue(e), 0);
                const approvedRev = coEntries.reduce((s, e) => s + e.costLines
                    .filter(l => (l.status || '').toLowerCase() === 'approved'
                        && String(l.costType || '').split(' ')[0] === '41000')
                    .reduce((x, l) => x + (isNaN(pNum(l.dollars)) ? 0 : pNum(l.dollars)), 0), 0);
                const totalValue = totalBudget + approvedRev;
                if (!totalBudget && !coRev) return null;

                const statLabel = { fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5 };
                const statValue = { fontSize: 16, fontWeight: '800', color: '#1e293b' };
                const opSign = { fontSize: 16, fontWeight: '600', color: '#94a3b8', marginTop: 12, marginLeft: 14, marginRight: 14 };

                return (
                    <View style={{
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        marginLeft: 20,
                        marginRight: 20,
                        marginBottom: 4,
                        padding: '8px 16px',
                        backgroundColor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 6,
                        alignSelf: 'flex-start',
                    }}>
                        <View>
                            <Text style={statLabel}>Bid Budget</Text>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={statValue}>{fNum(Math.round(totalBudget), 1)}</Text>
                                {contractStatus && (
                                    <Pressable
                                        title={`${contractStatus.label}\n${contractStatus.tooltip}`}
                                        style={{
                                            marginLeft: 6,
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            cursor: contractStatus.url ? 'pointer' : 'default',
                                        }}
                                        onPress={() => contractStatus.url && window.open(contractStatus.url, '_blank')}
                                    >
                                        <Icon3
                                            name={contractStatus.ok ? 'MdCheckCircleOutline' : 'MdWarningAmber'}
                                            size={16}
                                            color={contractStatus.ok ? '#16a34a' : '#d97706'}
                                        />
                                    </Pressable>
                                )}
                            </View>
                        </View>
                        <Text style={opSign}>+</Text>
                        <View>
                            <Text style={statLabel}>Approved CO's</Text>
                            <Text style={statValue} title={`All COs incl. pending: ${fNum(Math.round(coRev), 1)}`}>
                                {fNum(Math.round(approvedRev), 1)}
                            </Text>
                            {approvedRev !== coRev && (
                                <Text style={{ fontSize: 10, color: '#64748b' }}>
                                    {fNum(Math.round(coRev), 1)} incl. pending
                                </Text>
                            )}
                        </View>
                        <Text style={opSign}>=</Text>
                        <View>
                            <Text style={statLabel}>Current Budget</Text>
                            <Text style={statValue}>{fNum(Math.round(totalValue), 1)}</Text>
                        </View>
                    </View>
                );
            })()}

            <ScrollView>
                <View style={{ padding: '10px 20px', alignSelf: 'flex-start' }}>
                    {isLoading ? (
                        <View style={{ padding: 40, alignItems: 'center' }}>
                            <Text style={{ fontSize: 14, color: '#2563eb', fontWeight: '600' }}>Loading change orders...</Text>
                        </View>
                    ) : coEntries.length === 0 ? (
                        <View style={{ padding: 40, alignItems: 'center' }}>
                            <Text style={{ fontSize: 16, color: '#666' }}>
                                No change orders. Click "Add Change Order" to create one.
                            </Text>
                        </View>
                    ) : (<>
                        {/* Column Headers */}
                        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px', marginBottom: 4 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <View style={{ width: 16 }} />
                                <Text style={{ width: 64, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>CO</Text>
                                <Text style={{ width: 110, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>Status</Text>
                                <Text style={{ width: 120, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, marginRight: 8 }}>Type</Text>
                                <Text style={{ fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}>Description</Text>
                            </View>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={{ width: 80, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Labor Hrs</Text>
                                <Text style={{ width: 80, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Equip Hrs</Text>
                                <Text style={{ width: 80, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Quantity</Text>
                                <Text style={{ width: 110, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Revenue</Text>
                                <Text style={{ width: 110, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Cost</Text>
                                <Text style={{ width: 110, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right' }}>Job Prof</Text>
                                <Text style={{ width: 60, fontSize: 10, fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'right', marginLeft: 8 }}>Job %</Text>
                                <View style={{ width: 30 }} />
                            </View>
                        </View>
                        {coEntries.map((entry, entryIdx) => (
                            <COEntryCard
                                key={entry._localId}
                                entry={entry}
                                entryIdx={entryIdx}
                                updateEntry={updateEntry}
                                updateCostLine={updateCostLine}
                                addCostLine={addCostLine}
                                addCostLineWithValues={addCostLineWithValues}
                                removeCostLine={removeCostLine}
                                removeEntry={removeEntry}
                                saveEntry={saveEntry}
                                showDeleteConfirm={showDeleteConfirm}
                                setShowDeleteConfirm={setShowDeleteConfirm}
                                expandedCards={expandedCards}
                                setExpandedCards={setExpandedCards}
                                statusOptions={statusOptions}
                                typeOptions={typeOptions}
                                revenueTypeOptions={revenueTypeOptions}
                                costTypeDescriptions={costTypeDescriptions}
                                phaseCodes={phaseCodes}
                                jobPhaseCodes={jobPhaseCodes}
                                glValues={glValues}
                                resourceCodeOptions={resourceCodeOptions}
                                inputStyle={inputStyle}
                                selectStyle={selectStyle}
                                labelStyle={labelStyle}
                                COLORS={COLORS}
                                bw={bw}
                                isSaving={isSaving}
                                calcRevenue={calcRevenue}
                                calcCost={calcCost}
                                formatDate={formatDate}
                                missingDoc={isCoMissingDoc(entry)}
                                missingDocTooltip={coDocsTooltip}
                                renderSigBadge={renderSigBadge}
                            />
                        ))}
                    </>)}

                    {/* Summary Rows: Approved / Not Approved / Total */}
                    {coEntries.length > 0 && (() => {
                        // Filter at the cost-line level: blank status is NOT approved
                        const sumRowByLineStatus = (entries, lineFilter) => {
                            let rev = 0, cost = 0, laborHrs = 0, equipHrs = 0, totalQty = 0, count = 0;
                            entries.forEach(e => {
                                const lines = e.costLines.filter(lineFilter);
                                if (lines.length === 0) return;
                                count++;
                                lines.forEach(l => {
                                    const ct = l.costType ? String(l.costType).split(' ')[0] : '';
                                    const dollars = isNaN(pNum(l.dollars)) ? 0 : pNum(l.dollars);
                                    if (ct === '41000') rev += dollars;
                                    if (ct.startsWith('5')) cost += dollars;
                                    if (ct.startsWith('51')) laborHrs += (parseFloat(l.hours) || 0);
                                    if (ct.startsWith('52')) equipHrs += (parseFloat(l.hours) || 0);
                                    totalQty += (parseFloat(l.quantity) || 0);
                                });
                            });
                            const margin = rev - cost;
                            const pct = rev !== 0 ? ((margin / rev) * 100).toFixed(1) + '%' : '--';
                            return { rev, cost, margin, pct, laborHrs, equipHrs, totalQty, count };
                        };

                        const isApproved = l => (l.status || '').toLowerCase() === 'approved';
                        const isNotApproved = l => (l.status || '').toLowerCase() !== 'approved';

                        const appRow = sumRowByLineStatus(coEntries, isApproved);
                        const notRow = sumRowByLineStatus(coEntries, isNotApproved);
                        const allRow = sumRowByLineStatus(coEntries, () => true);

                        const renderSummaryRow = (label, data, bgColor, borderColor, isLast) => (
                            <View style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '4px 8px',
                                marginTop: isLast ? 2 : 0,
                                backgroundColor: bgColor,
                                borderTop: isLast ? '2px solid #64748b' : undefined,
                                borderBottom: !isLast ? `1px solid ${borderColor}` : undefined,
                                borderRadius: isLast ? 4 : 0,
                            }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', width: 16 + 64 + 8 + 110 + 8 + 120 + 8 }}>
                                    <View style={{ width: 16 }} />
                                    <Text style={{ fontSize: 11, fontWeight: isLast ? '800' : '700', color: '#1e293b', width: 64, marginRight: 8 }}>
                                    </Text>
                                    <Text style={{ fontSize: 11, fontWeight: isLast ? '800' : '700', color: '#475569', width: 110, marginRight: 8 }}>
                                        {label} ({data.count})
                                    </Text>
                                </View>
                                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: isLast ? '800' : '700', width: 80, textAlign: 'right' }}>
                                        {data.laborHrs ? fNum(data.laborHrs) : ''}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: isLast ? '800' : '700', width: 80, textAlign: 'right' }}>
                                        {data.equipHrs ? fNum(data.equipHrs) : ''}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: isLast ? '800' : '700', width: 80, textAlign: 'right' }}>
                                        {data.totalQty ? fNum(data.totalQty) : ''}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: isLast ? '800' : '700', width: 110, textAlign: 'right' }}>
                                        {fNum(Math.round(data.rev), 1)}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: isLast ? '800' : '700', width: 110, textAlign: 'right' }}>
                                        {fNum(Math.round(data.cost), 1)}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: data.margin >= 0 ? '#16a34a' : '#dc2626', fontWeight: isLast ? '800' : '700', width: 110, textAlign: 'right' }}>
                                        {fNum(Math.round(data.margin), 1)}
                                    </Text>
                                    <Text style={{ fontSize: 11, color: '#1e293b', fontWeight: isLast ? '800' : '700', width: 60, textAlign: 'right', marginLeft: 8 }}>
                                        {data.pct}
                                    </Text>
                                    <View style={{ width: 30 }} />
                                </View>
                            </View>
                        );

                        return (
                            <View style={{ marginTop: 6, borderRadius: 6, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                                {renderSummaryRow('Approved', appRow, '#eff6ff', '#bfdbfe', false)}
                                {renderSummaryRow('Not Approved', notRow, '#f0f5ff', '#c7d2fe', false)}
                                {renderSummaryRow('Total', allRow, '#f1f5f9', '#cbd5e1', true)}
                            </View>
                        );
                    })()}
                    <div ref={bottomRef} />
                </View>
            </ScrollView>
        </View>
    );
};

/**
 * Individual CO entry card - one row in the UI
 */
const COEntryCard = ({
    entry,
    entryIdx,
    updateEntry,
    updateCostLine,
    addCostLine,
    addCostLineWithValues,
    removeCostLine,
    removeEntry,
    saveEntry,
    showDeleteConfirm,
    setShowDeleteConfirm,
    expandedCards,
    setExpandedCards,
    statusOptions,
    typeOptions,
    revenueTypeOptions,
    costTypeDescriptions,
    phaseCodes,
    jobPhaseCodes,
    glValues,
    resourceCodeOptions,
    inputStyle,
    selectStyle,
    labelStyle,
    COLORS,
    bw,
    isSaving,
    calcRevenue,
    calcCost,
    formatDate,
    missingDoc,
    missingDocTooltip,
    renderSigBadge,
}) => {
    const revenue = calcRevenue(entry);
    const cost = calcCost(entry);
    const margin = revenue - cost;
    const laborHrs = entry.costLines.filter(l => { const ct = l.costType ? String(l.costType).split(' ')[0] : ''; return ct.startsWith('51'); }).reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
    const equipHrs = entry.costLines.filter(l => { const ct = l.costType ? String(l.costType).split(' ')[0] : ''; return ct.startsWith('52'); }).reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
    const totalQty = entry.costLines.reduce((s, l) => s + (parseFloat(l.quantity) || 0), 0);
    const isConfirmingDelete = showDeleteConfirm === entry._localId;
    const isExpanded = expandedCards[entry._localId] || false;
    const [lineViewMode, setLineViewMode] = useState('sections'); // 'lines' or 'sections'

    // Check if any cost lines have values (dollars/hours/quantity) but no status
    const hasLinesWithValueButNoStatus = entry.costLines.some(l => {
        const hasDollars = l.dollars && pNum(l.dollars) !== 0;
        const hasHours = l.hours && pNum(l.hours) !== 0;
        const hasQty = l.quantity && pNum(l.quantity) !== 0;
        return (hasDollars || hasHours || hasQty) && !l.status;
    });

    const statusChipStyle = {
        'Approved':              { bg: '#dcfce7', border: '#86efac', text: '#15803d' },
        'Pending - Performed':   { bg: '#fef9c3', border: '#fde047', text: '#a16207' },
        'Pending - Not Performed': { bg: '#ffedd5', border: '#fdba74', text: '#c2410c' },
        'Not Submitted':         { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569' },
        'Denied':                { bg: '#fee2e2', border: '#fca5a5', text: '#b91c1c' },
        'Passthru':              { bg: '#ede9fe', border: '#c4b5fd', text: '#6d28d9' },
    };
    const defaultChip = { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569' };

    const costTypeOptions = Object.entries(costTypeDescriptions).map(([code, desc]) => ({
        label: `${code} - ${desc}`,
        value: code,
    }));

    return (
        <View style={{
            backgroundColor: '#fff',
            borderRadius: 6,
            border: entry._dirty ? '2px solid #f59e0b' : '1px solid #e2e8f0',
            marginBottom: isExpanded ? 10 : 2,
            // The Lines grid keeps its column widths; on a narrow screen the card grows with it
            // and the page scrolls sideways. Sections scrolls inside its own box instead.
            ...(isExpanded && lineViewMode === 'lines' && { width: 'max-content', minWidth: '100%' }),
        }}>
            {/* Card Header - clickable to expand/collapse */}
            <Pressable
                onPress={() => setExpandedCards(prev => ({ ...prev, [entry._localId]: !isExpanded }))}
                style={{
                flexDirection: 'row',
                alignItems: 'center',
                padding: isExpanded ? '6px 8px' : '3px 8px',
                backgroundColor: entry._dirty ? '#fffbeb' : (isExpanded ? '#f8fafc' : 'transparent'),
                borderBottom: isExpanded ? '1px solid #e2e8f0' : undefined,
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
                borderBottomLeftRadius: isExpanded ? 0 : 6,
                borderBottomRightRadius: isExpanded ? 0 : 6,
                justifyContent: 'space-between',
                cursor: 'pointer',
            }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1, minWidth: 0 }}>
                    <Icon3 name={isExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={16} color="#94a3b8" />
                    <View style={{ width: 64, marginRight: 8, flexDirection: 'row', alignItems: 'center' }}>
                        <Text style={{ fontSize: 13, fontWeight: '700', color: '#1e293b' }}>
                            {entry.coNo || '--'}
                        </Text>
                        {missingDoc && (
                            <View style={{ marginLeft: 4 }} title={missingDocTooltip}>
                                <Icon3 name="MdWarning" size={14} color="#f59e0b" />
                            </View>
                        )}
                    </View>
                    <View style={{ width: 110, marginRight: 8 }}>
                        {(() => { const s = statusChipStyle[entry.status] || defaultChip; return (
                            <View style={{
                                backgroundColor: s.bg,
                                borderWidth: 1,
                                borderColor: s.border,
                                borderStyle: 'solid',
                                paddingLeft: 7, paddingRight: 7,
                                paddingTop: 1, paddingBottom: 1,
                                borderRadius: 3,
                                alignSelf: 'flex-start',
                            }}>
                                <Text style={{ fontSize: 10, fontWeight: '700', color: s.text }}>
                                    {entry.status || 'No Status'}
                                </Text>
                            </View>
                        ); })()}
                    </View>
                    <View style={{ width: 120, marginRight: 8 }}>
                        {entry.type ? (
                            <View style={{
                                backgroundColor: '#dbeafe',
                                borderWidth: 1,
                                borderColor: '#93c5fd',
                                borderStyle: 'solid',
                                paddingLeft: 7, paddingRight: 7,
                                paddingTop: 1, paddingBottom: 1,
                                borderRadius: 3,
                                alignSelf: 'flex-start',
                            }}>
                                <Text style={{ fontSize: 10, fontWeight: '700', color: '#1d4ed8' }}>
                                    {entry.type}
                                </Text>
                            </View>
                        ) : null}
                    </View>
                    <View style={{ flex: 1, minWidth: 100, overflow: 'hidden' }}>
                        {entry.description ? (
                            <Text style={{ fontSize: 11, color: '#64748b' }} numberOfLines={1} title={entry.description}>
                                {entry.description}
                            </Text>
                        ) : null}
                        {entry._dirty && (
                            <Text style={{ fontSize: 9, color: '#d97706', fontWeight: '500' }}>● Unsaved</Text>
                        )}
                    </View>
                </View>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 0 }}>
                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: '600', width: 80, textAlign: 'right' }}>
                        {laborHrs ? fNum(laborHrs) : ''}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: '600', width: 80, textAlign: 'right' }}>
                        {equipHrs ? fNum(equipHrs) : ''}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: '600', width: 80, textAlign: 'right' }}>
                        {totalQty ? fNum(totalQty) : ''}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: '600', width: 110, textAlign: 'right' }}>
                        {fNum(Math.round(revenue), 1)}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#334155', fontWeight: '600', width: 110, textAlign: 'right' }}>
                        {fNum(Math.round(cost), 1)}
                    </Text>
                    <Text style={{ fontSize: 11, color: margin >= 0 ? '#16a34a' : '#dc2626', fontWeight: '600', width: 110, textAlign: 'right' }}>
                        {fNum(Math.round(margin), 1)}
                    </Text>
                    <Text style={{ fontSize: 11, color: '#64748b', fontWeight: '600', width: 60, textAlign: 'right', marginLeft: 8 }}>
                        {revenue !== 0 ? ((margin / revenue) * 100).toFixed(1) + '%' : '--'}
                    </Text>
                    {hasLinesWithValueButNoStatus && (
                        <View style={{ marginLeft: 4, marginRight: 2 }} title="Some cost lines have values but no status">
                            <Icon3 name="MdWarning" size={16} color="#f59e0b" />
                        </View>
                    )}
                    {isConfirmingDelete ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#fef2f2', padding: '4px 10px', borderRadius: 6, border: '1px solid #fecaca' }}>
                            <Text style={{ fontSize: 12, color: '#dc2626', fontWeight: '600' }}>Delete CO #{entry.coNo || 'new'}?</Text>
                            <Pressable
                                style={{ backgroundColor: '#dc2626', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 }}
                                onPress={() => removeEntry(entry._localId)}
                            >
                                <Text style={{ color: '#fff', fontSize: 11, fontWeight: '700' }}>Yes</Text>
                            </Pressable>
                            <Pressable
                                style={{ backgroundColor: '#e2e8f0', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 4 }}
                                onPress={() => setShowDeleteConfirm(null)}
                            >
                                <Text style={{ color: '#475569', fontSize: 11, fontWeight: '600' }}>No</Text>
                            </Pressable>
                        </View>
                    ) : (
                        <Pressable
                            style={{ padding: 6, borderRadius: 4 }}
                            onPress={() => setShowDeleteConfirm(entry._localId)}
                        >
                            <Icon3 name="MdDelete" size={18} color="#94a3b8" />
                        </Pressable>
                    )}
                </View>
            </Pressable>

            {/* Collapsible Body */}
            {isExpanded && (<>
            {/* Main Fields Row */}
            <View style={{ padding: 16, paddingBottom: 8 }}>
                <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap' }}>
                    {/* CO Number */}
                    <View style={{ width: 80 }}>
                        <Text style={labelStyle}>CO No.</Text>
                        <TextInput
                            style={{ ...inputStyle, width: '100%' }}
                            value={entry.coNo}
                            onChangeText={(v) => updateEntry(entry._localId, 'coNo', v)}
                            placeholder="CO #"
                        />
                    </View>

                    {/* Description */}
                    <View style={{ flex: 1, minWidth: 200 }}>
                        <Text style={labelStyle}>Description</Text>
                        <TextInput
                            style={{ ...inputStyle, width: '100%' }}
                            value={entry.description}
                            onChangeText={(v) => updateEntry(entry._localId, 'description', v.slice(0, MAX_DESCRIPTION_LENGTH))}
                            placeholder="Description"
                            maxLength={MAX_DESCRIPTION_LENGTH}
                        />
                        {(entry.description || '').length >= MAX_DESCRIPTION_LENGTH - 20 && (
                            <Text style={{ fontSize: 10, color: (entry.description || '').length >= MAX_DESCRIPTION_LENGTH ? '#dc2626' : '#94a3b8', marginTop: 2 }}>
                                {(entry.description || '').length}/{MAX_DESCRIPTION_LENGTH}
                            </Text>
                        )}
                    </View>

                    {/* Status */}
                    <View style={{ width: 180 }}>
                        <Text style={labelStyle}>Status</Text>
                        <select
                            style={{ ...selectStyle, width: '100%', height: 32 }}
                            value={entry.status}
                            onChange={(e) => updateEntry(entry._localId, 'status', e.target.value)}
                        >
                            <option value="">-- Select --</option>
                            {statusOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </View>

                    {/* Type */}
                    <View style={{ width: 170 }}>
                        <Text style={labelStyle}>Type</Text>
                        <select
                            style={{ ...selectStyle, width: '100%', height: 32 }}
                            value={entry.type}
                            onChange={(e) => updateEntry(entry._localId, 'type', e.target.value)}
                        >
                            <option value="">-- Select --</option>
                            {typeOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </View>

                    {/* Revenue Type */}
                    <View style={{ width: 160 }}>
                        <Text style={labelStyle}>Revenue Type</Text>
                        <select
                            style={{ ...selectStyle, width: '100%', height: 32 }}
                            value={entry.revenueType}
                            onChange={(e) => updateEntry(entry._localId, 'revenueType', e.target.value)}
                        >
                            <option value="">-- Select --</option>
                            {revenueTypeOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </View>


                </View>

                {/* PSR Details - Always Visible */}
                <View style={{
                    flexDirection: 'row',
                    gap: 12,
                    flexWrap: 'wrap',
                    padding: 12,
                    backgroundColor: '#f8fafc',
                    borderRadius: 6,
                    border: '1px solid #e2e8f0',
                    marginTop: 10,
                    marginBottom: 8,
                }}>
                        {/* Initiated */}
                        <View style={{ width: 140 }}>
                            <Text style={labelStyle}>Initiated Date</Text>
                            <input
                                type="date"
                                style={{ ...inputStyle, width: '100%', height: 30 }}
                                value={formatDate(entry.initiated)}
                                onChange={(e) => updateEntry(entry._localId, 'initiated', e.target.value)}
                            />
                        </View>

                        {/* Submitted */}
                        <View style={{ width: 140 }}>
                            <Text style={labelStyle}>Submitted Date</Text>
                            <input
                                type="date"
                                style={{ ...inputStyle, width: '100%', height: 30 }}
                                value={formatDate(entry.submitted)}
                                onChange={(e) => updateEntry(entry._localId, 'submitted', e.target.value)}
                            />
                        </View>

                        {/* Signed */}
                        <View style={{ width: 160 }}>
                            <Text style={labelStyle}>Signed Date</Text>
                            <input
                                type="date"
                                style={{ ...inputStyle, width: '100%', height: 30 }}
                                value={formatDate(entry.signed)}
                                onChange={(e) => updateEntry(entry._localId, 'signed', e.target.value)}
                            />
                            {renderSigBadge(entry)}
                        </View>

                        {/* Extension */}
                        <View style={{ width: 100 }}>
                            <Text style={labelStyle}>Extension</Text>
                            <TextInput
                                style={{ ...inputStyle, width: '100%' }}
                                value={entry.extension ? entry.extension.toString() : ''}
                                onChangeText={(v) => updateEntry(entry._localId, 'extension', v)}
                                placeholder="0"
                            />
                        </View>

                        {/* Task checkbox */}
                        <View style={{ width: 60, justifyContent: 'flex-end' }}>
                            <Text style={labelStyle}>Task</Text>
                            <Pressable
                                style={{
                                    width: 24, height: 24,
                                    borderWidth: 2,
                                    borderColor: entry.task ? '#2563eb' : '#d1d5db',
                                    borderRadius: 4,
                                    backgroundColor: entry.task ? '#2563eb' : '#fff',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                onPress={() => updateEntry(entry._localId, 'task', !entry.task)}
                            >
                                {entry.task && <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700' }}>✓</Text>}
                            </Pressable>
                        </View>

                        {/* Critical Path checkbox */}
                        <View style={{ width: 80, justifyContent: 'flex-end' }}>
                            <Text style={labelStyle}>Critical Path</Text>
                            <Pressable
                                style={{
                                    width: 24, height: 24,
                                    borderWidth: 2,
                                    borderColor: entry.criticalPath ? '#2563eb' : '#d1d5db',
                                    borderRadius: 4,
                                    backgroundColor: entry.criticalPath ? '#2563eb' : '#fff',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                                onPress={() => updateEntry(entry._localId, 'criticalPath', !entry.criticalPath)}
                            >
                                {entry.criticalPath && <Text style={{ color: '#fff', fontSize: 14, fontWeight: '700' }}>✓</Text>}
                            </Pressable>
                        </View>

                        {/* Notes */}
                        <View style={{ flex: 1, minWidth: 200 }}>
                            <Text style={labelStyle}>Notes</Text>
                            <TextInput
                                style={{ ...inputStyle, width: '100%' }}
                                value={entry.notes}
                                onChangeText={(v) => updateEntry(entry._localId, 'notes', v)}
                                placeholder="Additional notes..."
                                multiline={false}
                            />
                        </View>
                    </View>
            </View>

            {/* Cost Lines / Sections Toggle + Content */}
            <View style={{ padding: '0 16px 16px 16px' }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6, justifyContent: 'space-between' }}>
                    {/* Lines / Sections toggle */}
                    <View style={{ flexDirection: 'row', borderRadius: 5, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                        <Pressable
                            onPress={() => setLineViewMode('lines')}
                            style={{ padding: '4px 10px', backgroundColor: lineViewMode === 'lines' ? '#2563eb' : '#f8fafc', flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: lineViewMode === 'lines' ? '#fff' : '#64748b' }}>Lines</Text>
                            {hasLinesWithValueButNoStatus && (
                                <Icon3 name="MdWarning" size={14} color={lineViewMode === 'lines' ? '#fde68a' : '#f59e0b'} />
                            )}
                        </Pressable>
                        <Pressable
                            onPress={() => setLineViewMode('sections')}
                            style={{ padding: '4px 10px', backgroundColor: lineViewMode === 'sections' ? '#2563eb' : '#f8fafc' }}>
                            <Text style={{ fontSize: 11, fontWeight: '600', color: lineViewMode === 'sections' ? '#fff' : '#64748b' }}>Sections</Text>
                        </Pressable>
                    </View>

                    {lineViewMode === 'lines' && (
                        <Pressable
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                backgroundColor: '#eff6ff',
                                paddingHorizontal: 10,
                                paddingVertical: 4,
                                borderRadius: 4,
                                border: '1px solid #bfdbfe',
                            }}
                            onPress={() => addCostLine(entry._localId)}
                        >
                            <Icon3 name="MdAdd" size={14} color="#2563eb" />
                            <Text style={{ fontSize: 11, color: '#2563eb', fontWeight: '600', marginLeft: 2 }}>Add Line</Text>
                        </Pressable>
                    )}
                </View>

                {lineViewMode === 'lines' ? (<View style={{ width: 'max-content', minWidth: '100%' }}>
                {/* Cost lines header */}
                <View style={{
                    flexDirection: 'row',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '2px solid #cbd5e1',
                    paddingVertical: 6,
                    paddingHorizontal: 4,
                }}>
                    <Text style={{ width: 130, fontSize: 11, fontWeight: '700', color: '#475569' }}>Phase</Text>
                    <Text style={{ width: 210, fontSize: 11, fontWeight: '700', color: '#475569' }}>Phase Description</Text>
                    <Text style={{ width: 200, fontSize: 11, fontWeight: '700', color: '#475569' }}>Cost Type</Text>
                    <Text style={{ width: 130, fontSize: 11, fontWeight: '700', color: '#475569' }}>Status</Text>
                    <Text style={{ width: 110, fontSize: 11, fontWeight: '700', color: '#475569', textAlign: 'right' }}>Dollars</Text>
                    <Text style={{ width: 90, fontSize: 11, fontWeight: '700', color: '#475569', textAlign: 'right' }}>Hours</Text>
                    <Text style={{ width: 90, fontSize: 11, fontWeight: '700', color: '#475569', textAlign: 'right' }}>Quantity</Text>
                    <Text style={{ width: 120, fontSize: 11, fontWeight: '700', color: '#475569' }}>Resource Code</Text>
                    <Text style={{ width: 40, fontSize: 11, fontWeight: '700', color: '#475569', textAlign: 'center' }}></Text>
                </View>

                {/* Cost lines */}
                {entry.costLines.map((line, lineIdx) => (
                    <View
                        key={lineIdx}
                        style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 4,
                            paddingHorizontal: 4,
                            borderBottom: '1px solid #f1f5f9',
                            backgroundColor: lineIdx % 2 === 0 ? '#fff' : '#fafafa',
                        }}
                    >
                        {/* Phase dropdown */}
                        <View style={{ width: 130 }}>
                            <select
                                style={{ ...selectStyle, width: 120, height: 28, fontSize: 12 }}
                                value={line.costCode || ''}
                                onChange={(e) => updateCostLine(entry._localId, lineIdx, 'costCode', e.target.value)}
                            >
                                <option value="">-- Phase --</option>
                                {phaseCodes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                        </View>

                        {/* Phase description (read-only, from the job's chart) */}
                        <View style={{ width: 210, paddingRight: 10 }}>
                            <Text style={{ fontSize: 12, color: '#475569' }} numberOfLines={1}>
                                {(line.costCode && jobPhaseCodes?.[line.costCode]) || ''}
                            </Text>
                        </View>

                        {/* Cost Type dropdown */}
                        <View style={{ width: 200 }}>
                            <select
                                style={{ ...selectStyle, width: 190, height: 28, fontSize: 12 }}
                                value={line.costType}
                                onChange={(e) => updateCostLine(entry._localId, lineIdx, 'costType', e.target.value)}
                            >
                                <option value="">-- Select --</option>
                                {costTypeOptions.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </View>

                        {/* Status */}
                        <View style={{ width: 130 }}>
                            <select
                                style={{ ...selectStyle, width: 120, height: 28, fontSize: 12 }}
                                value={line.status || ''}
                                onChange={(e) => updateCostLine(entry._localId, lineIdx, 'status', e.target.value)}
                            >
                                <option value="">-- Status --</option>
                                {statusOptions.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </View>

                        {/* Dollars */}
                        <View style={{ width: 110 }}>
                            <TextInput
                                style={{ ...inputStyle, width: 100, textAlign: 'right', fontSize: 12, height: 28 }}
                                value={line.dollars ? line.dollars.toString() : ''}
                                onChangeText={(v) => updateCostLine(entry._localId, lineIdx, 'dollars', v)}
                                placeholder="0"
                            />
                        </View>

                        {/* Hours */}
                        <View style={{ width: 90 }}>
                            <TextInput
                                style={{ ...inputStyle, width: 80, textAlign: 'right', fontSize: 12, height: 28 }}
                                value={line.hours ? line.hours.toString() : ''}
                                onChangeText={(v) => updateCostLine(entry._localId, lineIdx, 'hours', v)}
                                placeholder="0"
                            />
                        </View>

                        {/* Quantity */}
                        <View style={{ width: 90 }}>
                            <TextInput
                                style={{ ...inputStyle, width: 80, textAlign: 'right', fontSize: 12, height: 28 }}
                                value={line.quantity ? line.quantity.toString() : ''}
                                onChangeText={(v) => updateCostLine(entry._localId, lineIdx, 'quantity', v)}
                                placeholder="0"
                            />
                        </View>

                        {/* Resource Code */}
                        <View style={{ width: 120 }}>
                            <ResourceCodeSelect
                                value={line.resourceCode || ''}
                                options={resourceCodeOptions}
                                onChange={(v) => updateCostLine(entry._localId, lineIdx, 'resourceCode', v)}
                                width={110}
                            />
                        </View>

                        {/* Remove line button */}
                        <View style={{ width: 40, alignItems: 'center' }}>
                            <Pressable
                                style={{ padding: 4 }}
                                onPress={() => removeCostLine(entry._localId, lineIdx)}
                            >
                                <Icon3 name="MdClose" size={16} color="#94a3b8" />
                            </Pressable>
                        </View>
                    </View>
                ))}
                </View>) : (
                /* ===== SECTIONS VIEW - Editable by Phase, like colog.js ===== */
                <SectionsView
                    entry={entry}
                    updateCostLine={updateCostLine}
                    addCostLine={addCostLine}
                    addCostLineWithValues={addCostLineWithValues}
                    removeCostLine={removeCostLine}
                    phaseCodes={phaseCodes}
                    jobPhaseCodes={jobPhaseCodes}
                    glValues={glValues}
                    costTypeDescriptions={costTypeDescriptions}
                    inputStyle={inputStyle}
                    selectStyle={selectStyle}
                    pNum={pNum}
                    fNum={fNum}
                />
                )}
            </View>
            </>)}
        </View>
    );
};

/**
 * SectionsView - Editable grid organized by Phase rows, with columns for
 * Revenue, Hours, Labor (expandable to show 51100 Reg Time etc.), Materials, Subs, Other.
 * Like the colog.js spreadsheet view but inside each CO entry card.
 */
const SectionsView = ({ entry, updateCostLine, addCostLine, addCostLineWithValues, removeCostLine, phaseCodes, jobPhaseCodes, glValues, costTypeDescriptions, inputStyle, selectStyle, pNum, fNum }) => {
    const [laborExpanded, setLaborExpanded] = useState(false);
    const [equipmentExpanded, setEquipmentExpanded] = useState(false);
    const [materialsExpanded, setMaterialsExpanded] = useState(false);
    const [subsExpanded, setSubsExpanded] = useState(false);
    const [otherExpanded, setOtherExpanded] = useState(false);
    const [focusedCell, setFocusedCell] = useState(null);
    const [phaseSearch, setPhaseSearch] = useState('');
    const [phaseDropdownOpen, setPhaseDropdownOpen] = useState(false);

    const lines = entry.costLines || [];

    // Group lines by phase (costCode)
    const phaseGroups = {};
    lines.forEach((line, lineIdx) => {
        const phase = line.costCode || '(No Phase)';
        if (!phaseGroups[phase]) phaseGroups[phase] = [];
        phaseGroups[phase].push({ ...line, _lineIdx: lineIdx });
    });
    const phaseKeys = Object.keys(phaseGroups).sort((a, b) => {
        if (a === '(No Phase)') return 1;
        if (b === '(No Phase)') return -1;
        return a.localeCompare(b);
    });

    // Helper: sum dollars for lines matching cost type prefixes
    const sumDollars = (pLines, prefixes) =>
        pLines.filter(l => { const ct = l.costType ? String(l.costType).split(' ')[0] : ''; return prefixes.some(p => ct.startsWith(p)); })
            .reduce((s, l) => s + (isNaN(pNum(l.dollars)) ? 0 : pNum(l.dollars)), 0);

    const sumHours = (pLines, prefixes) =>
        pLines.filter(l => { const ct = l.costType ? String(l.costType).split(' ')[0] : ''; return prefixes.some(p => ct.startsWith(p)); })
            .reduce((s, l) => s + (isNaN(pNum(l.hours)) ? 0 : pNum(l.hours)), 0);

    const sumQty = (pLines, prefixes) =>
        pLines.filter(l => { const ct = l.costType ? String(l.costType).split(' ')[0] : ''; return prefixes.some(p => ct.startsWith(p)); })
            .reduce((s, l) => s + (isNaN(pNum(l.quantity)) ? 0 : pNum(l.quantity)), 0);

    // Labor sub-codes
    const laborCodes = [
        { code: '51100', label: 'Regular Time' }, { code: '51200', label: 'Overtime' },
        { code: '51300', label: 'Burden' }, { code: '51400', label: 'BYOD' },
        { code: '51500', label: 'Per Diem' }, { code: '51600', label: 'Truck Pay' },
        { code: '51700', label: 'Welding Rig Pay' }, { code: '51800', label: 'Field Bonus' },
    ];

    const equipmentCodes = [
        { code: '52100', label: 'Inside Equip Rental' }, { code: '52200', label: 'Outside Equip Rental' },
        { code: '52300', label: 'Fuel' }, { code: '52400', label: 'Repairs' },
    ];

    const materialsCodes = [
        { code: '53100', label: 'Materials' }, { code: '53200', label: 'Mats' },
        { code: '53300', label: 'Tools/Supplies' }, { code: '53310', label: 'Tools/Supplies Purch' },
        { code: '53400', label: 'Skids' }, { code: '53500', label: 'Small Tools' },
    ];

    const subsCodes = [
        { code: '54100', label: 'Internal Subs' }, { code: '54200', label: 'External Subs' },
        { code: '54300', label: 'Hauling/Transport' },
    ];

    const otherCodes = [
        { code: '55100', label: 'Personal Meals' }, { code: '55200', label: 'Team Meals' },
        { code: '55300', label: 'Airfare' }, { code: '55400', label: 'Lodging' },
        { code: '55900', label: 'Other Travel' }, { code: '56100', label: 'Field Office Rent' },
        { code: '56200', label: 'Field Office Util' }, { code: '56300', label: 'Field Office Exp' },
        { code: '56400', label: 'Lab Fees/Testing' }, { code: '56500', label: 'Safety/PPE' },
        { code: '56600', label: 'Bonds/Insurance' }, { code: '56700', label: 'Permits' },
        { code: '56800', label: 'Insurance Claims' }, { code: '57100', label: 'Taxes' },
        { code: '57200', label: 'Accrued Expenses' }, { code: '57300', label: 'Warranty Reserve' },
        { code: '57900', label: 'Misc' },
    ];

    const hdr = { fontSize: 10, fontWeight: '700', color: '#475569', textAlign: 'right' };
    const cw = 100; // column width

    // Build set of valid costCode|costType combos from GL data for quick lookup
    const validCombosSet = React.useMemo(() => {
        if (!glValues || !glValues.length) return null;
        const s = new Set();
        glValues.forEach(d => {
            const cc = (d.Cost_Code || '').toString().trim();
            const ct = (d.Cost_Type || '').toString().trim();
            if (cc && ct) s.add(`${cc}|${ct}`);
        });
        return s;
    }, [glValues]);

    // Check if a phase+costType combo is valid (returns true if no GL data loaded or combo exists)
    // GL data: Cost_Code = cost type (e.g. 51100), Cost_Type = phase/subsidiary (e.g. 010100)
    const isCostTypeValidForPhase = (phaseCode, costType) => {
        if (!validCombosSet || phaseCode === '(No Phase)' || !phaseCode) return true;
        return validCombosSet.has(`${costType}|${phaseCode}`);
    };

    // Helper: update a specific cost type line's dollars for a phase, or create it
    const updateOrCreateLine = (phaseCode, costType, field, value) => {
        const existingLine = lines.findIndex((l) => {
            const ct = l.costType ? String(l.costType).split(' ')[0] : '';
            return ct === costType && (l.costCode || '') === (phaseCode === '(No Phase)' ? '' : phaseCode);
        });
        if (existingLine >= 0) {
            updateCostLine(entry._localId, existingLine, field, value);
        } else {
            // Create line with all values atomically to avoid race conditions
            addCostLineWithValues(entry._localId, {
                costType,
                costCode: phaseCode === '(No Phase)' ? '' : phaseCode,
                [field]: value,
                ...(entry.status ? { status: entry.status } : {})
            });
        }
    };

    // Editable cell — sums all matching lines for display; on edit, updates first match (zeros duplicates)
    const renderEditCell = (phaseCode, costType, field, width) => {
        const matchingLines = lines.map((l, idx) => ({ ...l, _lineIdx: idx }))
            .filter(l => {
                const ct = l.costType ? String(l.costType).split(' ')[0] : '';
                return ct === costType && (l.costCode || '') === (phaseCode === '(No Phase)' ? '' : phaseCode);
            });
        const currentSum = matchingLines.reduce((s, l) => s + (isNaN(pNum(l[field])) ? 0 : pNum(l[field])), 0);
        const cellKey = `${phaseCode}-${costType}-${field}`;
        const isFocused = focusedCell === cellKey;
        const val = currentSum ? currentSum.toString() : '';
        const displayVal = !isFocused && val && !isNaN(pNum(val)) ? fNum(Math.round(pNum(val)), 1) : val;
        const isDisabled = costType !== '41000' && !isCostTypeValidForPhase(phaseCode, costType);
        return (
            <View key={cellKey} style={{ width: width || cw }}>
                <TextInput
                    style={{
                        ...inputStyle,
                        width: (width || cw) - 8,
                        textAlign: 'right',
                        fontSize: 11,
                        height: 26,
                        ...(isDisabled ? { backgroundColor: '#e2e8f0', color: '#94a3b8', cursor: 'not-allowed' } : {}),
                    }}
                    value={displayVal}
                    editable={!isDisabled}
                    onFocus={() => setFocusedCell(cellKey)}
                    onBlur={() => setFocusedCell(null)}
                    onChangeText={(v) => {
                        if (isDisabled) return;
                        if (matchingLines.length > 0) {
                            updateCostLine(entry._localId, matchingLines[0]._lineIdx, field, v);
                            for (let i = 1; i < matchingLines.length; i++) {
                                updateCostLine(entry._localId, matchingLines[i]._lineIdx, field, '0');
                            }
                        } else if (v && v !== '0') {
                            updateOrCreateLine(phaseCode, costType, field, v);
                        }
                    }}
                    placeholder={isDisabled ? '' : '0'}
                />
            </View>
        );
    };

    // Editable cell for summed multi-line fields (hours, quantity across multiple cost type lines)
    // When user edits, the full value goes on the first matching line; other matching lines get zeroed out.
    const renderSumEditCell = (phaseCode, prefixes, field, width, defaultCostType) => {
        const matchingLines = lines.map((l, idx) => ({ ...l, _lineIdx: idx }))
            .filter(l => {
                const ct = l.costType ? String(l.costType).split(' ')[0] : '';
                return prefixes.some(p => ct.startsWith(p)) && (l.costCode || '') === (phaseCode === '(No Phase)' ? '' : phaseCode);
            });
        const currentSum = matchingLines.reduce((s, l) => s + (isNaN(pNum(l[field])) ? 0 : pNum(l[field])), 0);
        const cellKey = `${phaseCode}-SUM-${prefixes[0]}-${field}`;
        const isFocused = focusedCell === cellKey;
        const val = currentSum ? currentSum.toString() : '';
        const displayVal = !isFocused && val && !isNaN(pNum(val)) ? fNum(Math.round(pNum(val)), 1) : val;
        return (
            <View key={cellKey} style={{ width: width || cw }}>
                <TextInput
                    style={{ ...inputStyle, width: (width || cw) - 8, textAlign: 'right', fontSize: 11, height: 26 }}
                    value={displayVal}
                    onFocus={() => setFocusedCell(cellKey)}
                    onBlur={() => setFocusedCell(null)}
                    onChangeText={(v) => {
                        if (matchingLines.length > 0) {
                            // Put value on first line, zero out the rest
                            updateCostLine(entry._localId, matchingLines[0]._lineIdx, field, v);
                            for (let i = 1; i < matchingLines.length; i++) {
                                updateCostLine(entry._localId, matchingLines[i]._lineIdx, field, '0');
                            }
                        } else if (v && v !== '0') {
                            // No matching lines — create one with the default cost type
                            updateOrCreateLine(phaseCode, defaultCostType, field, v);
                        }
                    }}
                    placeholder="0"
                />
            </View>
        );
    };

    // Read-only summary cell
    const SumCell = ({ value, width, bold, color }) => (
        <Text style={{ width: width || cw, fontSize: 11, fontWeight: bold ? '700' : '500', color: color || '#334155', textAlign: 'right', paddingRight: 4 }}>
            {fNum(Math.round(value), 1)}
        </Text>
    );

    // Add phase row
    const addPhaseRow = (phaseCode) => {
        // Add a new blank cost line with this phase pre-set
        addCostLine(entry._localId);
        setTimeout(() => {
            const newIdx = entry.costLines.length;
            updateCostLine(entry._localId, newIdx, 'costCode', phaseCode);
        }, 0);
    };

    return (
        <View style={{ flex: 1 }}>
            {/* Add phase row - searchable dropdown (outside ScrollView so dropdown isn't clipped) */}
            <View style={{ flexDirection: 'row', alignItems: 'center', padding: '6px 4px', borderBottom: '1px solid #e2e8f0', position: 'relative', zIndex: 10 }}>
                <View style={{ position: 'relative', width: 220 }}>
                    <input
                        type="text"
                        placeholder="+ Add Phase (search...)"
                        value={phaseSearch}
                        onChange={(e) => { setPhaseSearch(e.target.value); setPhaseDropdownOpen(true); }}
                        onFocus={() => setPhaseDropdownOpen(true)}
                        onBlur={() => setTimeout(() => setPhaseDropdownOpen(false), 150)}
                        style={{ ...selectStyle, width: 220, height: 26, fontSize: 11, paddingLeft: 6, boxSizing: 'border-box' }}
                    />
                    {phaseDropdownOpen && (() => {
                        const searchLower = phaseSearch.toLowerCase();
                        const filtered = phaseCodes
                            .filter(c => !phaseGroups[c])
                            .filter(code => {
                                if (!searchLower) return true;
                                const desc = jobPhaseCodes?.[code] || '';
                                return code.toLowerCase().includes(searchLower) || desc.toLowerCase().includes(searchLower);
                            });
                        if (filtered.length === 0) return null;
                        return (
                            <div style={{
                                position: 'absolute', top: 28, left: 0, width: 280,
                                maxHeight: 200, overflowY: 'auto',
                                background: '#fff', border: '1px solid #cbd5e1', borderRadius: 4,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100,
                            }}>
                                {filtered.map(code => (
                                    <div
                                        key={code}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            addPhaseRow(code);
                                            setPhaseSearch('');
                                            setPhaseDropdownOpen(false);
                                        }}
                                        style={{
                                            padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                                            borderBottom: '1px solid #f1f5f9',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2fe'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                                    >
                                        {code} - {jobPhaseCodes?.[code] || ''}
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </View>
            </View>
        <ScrollView horizontal style={{ flex: 1 }}>
            <View>
                {/* Column Headers */}
                <View style={{
                    flexDirection: 'row',
                    backgroundColor: '#f1f5f9',
                    borderBottom: '2px solid #cbd5e1',
                    paddingVertical: 5,
                    paddingHorizontal: 4,
                    alignItems: 'center',
                }}>
                    <Text style={{ width: 180, fontSize: 10, fontWeight: '700', color: '#475569' }}>Phase</Text>
                    <Text style={{ ...hdr, width: cw }}>Revenue</Text>
                    <Text style={{ ...hdr, width: 70 }}>Labor Hrs</Text>
                    <Text style={{ ...hdr, width: 70 }}>Equip Hrs</Text>
                    <Text style={{ ...hdr, width: 70 }}>Quantity</Text>
                    {/* Labor - expandable */}
                    <Pressable onPress={() => setLaborExpanded(!laborExpanded)} style={{ flexDirection: 'row', alignItems: 'center', width: cw, justifyContent: 'space-between', paddingRight: 4 }}>
                        <Text style={{ ...hdr, flex: 1 }}>Labor</Text>
                        <Icon3 name={laborExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={14} color="#475569" />
                    </Pressable>
                    {laborExpanded && laborCodes.map(lc => (
                        <View key={lc.code} style={{ width: cw, alignItems: 'flex-end', paddingRight: 2 }}>
                            <Text style={{ ...hdr, fontSize: 9 }}>{lc.code}</Text>
                            <Text style={{ fontSize: 8, color: '#64748b' }}>{lc.label}</Text>
                        </View>
                    ))}
                    {/* Equipment - expandable */}
                    <Pressable onPress={() => setEquipmentExpanded(!equipmentExpanded)} style={{ flexDirection: 'row', alignItems: 'center', width: cw, justifyContent: 'space-between', paddingRight: 4 }}>
                        <Text style={{ ...hdr, flex: 1 }}>Equipment</Text>
                        <Icon3 name={equipmentExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={14} color="#475569" />
                    </Pressable>
                    {equipmentExpanded && equipmentCodes.map(ec => (
                        <View key={ec.code} style={{ width: cw, alignItems: 'flex-end', paddingRight: 2 }}>
                            <Text style={{ ...hdr, fontSize: 9 }}>{ec.code}</Text>
                            <Text style={{ fontSize: 8, color: '#64748b' }}>{ec.label}</Text>
                        </View>
                    ))}
                    {/* Materials - expandable */}
                    <Pressable onPress={() => setMaterialsExpanded(!materialsExpanded)} style={{ flexDirection: 'row', alignItems: 'center', width: cw, justifyContent: 'space-between', paddingRight: 4 }}>
                        <Text style={{ ...hdr, flex: 1 }}>Materials</Text>
                        <Icon3 name={materialsExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={14} color="#475569" />
                    </Pressable>
                    {materialsExpanded && materialsCodes.map(mc => (
                        <View key={mc.code} style={{ width: cw, alignItems: 'flex-end', paddingRight: 2 }}>
                            <Text style={{ ...hdr, fontSize: 9 }}>{mc.code}</Text>
                            <Text style={{ fontSize: 8, color: '#64748b' }}>{mc.label}</Text>
                        </View>
                    ))}
                    {/* Subs - expandable */}
                    <Pressable onPress={() => setSubsExpanded(!subsExpanded)} style={{ flexDirection: 'row', alignItems: 'center', width: cw, justifyContent: 'space-between', paddingRight: 4 }}>
                        <Text style={{ ...hdr, flex: 1 }}>Subs</Text>
                        <Icon3 name={subsExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={14} color="#475569" />
                    </Pressable>
                    {subsExpanded && subsCodes.map(sc => (
                        <View key={sc.code} style={{ width: cw, alignItems: 'flex-end', paddingRight: 2 }}>
                            <Text style={{ ...hdr, fontSize: 9 }}>{sc.code}</Text>
                            <Text style={{ fontSize: 8, color: '#64748b' }}>{sc.label}</Text>
                        </View>
                    ))}
                    {/* Other - expandable */}
                    <Pressable onPress={() => setOtherExpanded(!otherExpanded)} style={{ flexDirection: 'row', alignItems: 'center', width: cw, justifyContent: 'space-between', paddingRight: 4 }}>
                        <Text style={{ ...hdr, flex: 1 }}>Other</Text>
                        <Icon3 name={otherExpanded ? 'MdExpandMore' : 'MdChevronRight'} size={14} color="#475569" />
                    </Pressable>
                    {otherExpanded && otherCodes.map(oc => (
                        <View key={oc.code} style={{ width: cw, alignItems: 'flex-end', paddingRight: 2 }}>
                            <Text style={{ ...hdr, fontSize: 9 }}>{oc.code}</Text>
                            <Text style={{ fontSize: 8, color: '#64748b' }}>{oc.label}</Text>
                        </View>
                    ))}
                    <Text style={{ ...hdr, width: cw }}>Total Cost</Text>
                    <Text style={{ ...hdr, width: 80 }}>Job Prof</Text>
                    <Text style={{ ...hdr, width: 60 }}>Job %</Text>
                </View>

                {/* Phase rows */}
                {phaseKeys.map((phase, pIdx) => {
                    const pLines = phaseGroups[phase];
                    const rev = sumDollars(pLines, ['41000']);
                    const laborHrs = sumHours(pLines, ['51']);
                    const equipHrs = sumHours(pLines, ['52']);
                    const qty = sumQty(pLines, ['41', '51', '52', '53', '54', '55', '56', '57']);
                    const labor = sumDollars(pLines, ['511', '512', '513', '514', '515', '516', '517', '518']);
                    const equip = sumDollars(pLines, ['521', '522', '523', '524']);
                    const mat = sumDollars(pLines, ['531', '532', '533', '534']);
                    const subs = sumDollars(pLines, ['541', '542', '543']);
                    const other = sumDollars(pLines, ['55', '56', '57']);
                    const totalCost = labor + equip + mat + subs + other;
                    const margin = rev - totalCost;

                    return (
                        <View key={phase} style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            paddingVertical: 3,
                            paddingHorizontal: 4,
                            borderBottom: '1px solid #f1f5f9',
                            backgroundColor: pIdx % 2 === 0 ? '#fff' : '#fafafa',
                        }}>
                            <View style={{ width: 180, flexDirection: 'row', alignItems: 'center' }}>
                                <Pressable
                                    onPress={() => {
                                        const indices = pLines.map(l => l._lineIdx).sort((a, b) => b - a);
                                        indices.forEach(idx => removeCostLine(entry._localId, idx));
                                    }}
                                    style={{ marginRight: 4, padding: 2 }}
                                >
                                    <Icon3 name="MdClose" size={14} color="#94a3b8" />
                                </Pressable>
                                <Text style={{ fontSize: 11, fontWeight: '600', color: '#1e293b', flex: 1 }} numberOfLines={1}>
                                    {phase !== '(No Phase)' ? `${phase} - ${jobPhaseCodes?.[phase] || ''}` : phase}
                                </Text>
                            </View>
                            {/* Revenue */}
                            {renderEditCell(phase, '41000', 'dollars')}
                            {/* Labor Hours */}
                            {renderSumEditCell(phase, ['51'], 'hours', 70, '51100')}
                            {/* Equipment Hours */}
                            {renderSumEditCell(phase, ['52'], 'hours', 70, '52100')}
                            {/* Quantity */}
                            {renderSumEditCell(phase, ['41', '51', '52', '53', '54', '55', '56', '57'], 'quantity', 70, '51100')}
                            {/* Labor total (read-only summary) */}
                            <SumCell value={labor} />
                            {/* Labor expanded sub-columns */}
                            {laborExpanded && laborCodes.map(lc => (
                                renderEditCell(phase, lc.code, 'dollars')
                            ))}
                            {/* Equipment total */}
                            <SumCell value={equip} />
                            {/* Equipment expanded sub-columns */}
                            {equipmentExpanded && equipmentCodes.map(ec => (
                                renderEditCell(phase, ec.code, 'dollars')
                            ))}
                            {/* Materials total */}
                            <SumCell value={mat} />
                            {/* Materials expanded sub-columns */}
                            {materialsExpanded && materialsCodes.map(mc => (
                                renderEditCell(phase, mc.code, 'dollars')
                            ))}
                            {/* Subs total */}
                            <SumCell value={subs} />
                            {/* Subs expanded sub-columns */}
                            {subsExpanded && subsCodes.map(sc => (
                                renderEditCell(phase, sc.code, 'dollars')
                            ))}
                            {/* Other total */}
                            <SumCell value={other} />
                            {/* Other expanded sub-columns */}
                            {otherExpanded && otherCodes.map(oc => (
                                renderEditCell(phase, oc.code, 'dollars')
                            ))}
                            {/* Total Cost (read-only) */}
                            <SumCell value={totalCost} bold />
                            {/* Margin */}
                            <SumCell value={margin} width={80} bold color={margin >= 0 ? '#16a34a' : '#dc2626'} />
                            {/* Margin % */}
                            <Text style={{ width: 60, fontSize: 11, fontWeight: '600', color: '#64748b', textAlign: 'right', paddingRight: 4 }}>
                                {rev !== 0 ? ((margin / rev) * 100).toFixed(1) + '%' : '--'}
                            </Text>
                        </View>
                    );
                })}

                {/* Totals row */}
                {(() => {
                    const allRev = sumDollars(lines, ['41000']);
                    const allLabor = sumDollars(lines, ['511', '512', '513', '514', '515', '516', '517', '518']);
                    const allEquip = sumDollars(lines, ['521', '522', '523', '524']);
                    const allMat = sumDollars(lines, ['531', '532', '533', '534']);
                    const allSubs = sumDollars(lines, ['541', '542', '543']);
                    const allOther = sumDollars(lines, ['55', '56', '57']);
                    const allLaborHrs = sumHours(lines, ['51']);
                    const allEquipHrs = sumHours(lines, ['52']);
                    const allQty = sumQty(lines, ['41', '51', '52', '53', '54', '55', '56', '57']);
                    const allTotalCost = allLabor + allEquip + allMat + allSubs + allOther;
                    const allMargin = allRev - allTotalCost;
                    return (
                        <View style={{
                            flexDirection: 'row', alignItems: 'center', paddingVertical: 5, paddingHorizontal: 4,
                            borderTop: '2px solid #64748b', backgroundColor: '#f1f5f9',
                        }}>
                            <Text style={{ width: 180, fontSize: 11, fontWeight: '800', color: '#1e293b' }}>Total</Text>
                            <SumCell value={allRev} bold />
                            <SumCell value={allLaborHrs} bold width={70} />
                            <SumCell value={allEquipHrs} bold width={70} />
                            <SumCell value={allQty} bold width={70} />
                            <SumCell value={allLabor} bold />
                            {laborExpanded && laborCodes.map(lc => (
                                <SumCell key={lc.code} value={sumDollars(lines, [lc.code])} bold />
                            ))}
                            <SumCell value={allEquip} bold />
                            {equipmentExpanded && equipmentCodes.map(ec => (
                                <SumCell key={ec.code} value={sumDollars(lines, [ec.code])} bold />
                            ))}
                            <SumCell value={allMat} bold />
                            {materialsExpanded && materialsCodes.map(mc => (
                                <SumCell key={mc.code} value={sumDollars(lines, [mc.code])} bold />
                            ))}
                            <SumCell value={allSubs} bold />
                            {subsExpanded && subsCodes.map(sc => (
                                <SumCell key={sc.code} value={sumDollars(lines, [sc.code])} bold />
                            ))}
                            <SumCell value={allOther} bold />
                            {otherExpanded && otherCodes.map(oc => (
                                <SumCell key={oc.code} value={sumDollars(lines, [oc.code])} bold />
                            ))}
                            <SumCell value={allTotalCost} bold />
                            <SumCell value={allMargin} width={80} bold color={allMargin >= 0 ? '#16a34a' : '#dc2626'} />
                            <Text style={{ width: 60, fontSize: 11, fontWeight: '800', color: '#64748b', textAlign: 'right', paddingRight: 4 }}>
                                {allRev !== 0 ? ((allMargin / allRev) * 100).toFixed(1) + '%' : '--'}
                            </Text>
                        </View>
                    );
                })()}

            </View>
        </ScrollView>
        </View>
    );
};

export default COLogEntry;
